<template>
  <div class="tabContent" data-app>
    <div style="width: 100%; height: 100%">
      <div
        id="map"
        ref="map"
        class="map"
        :class="{
          'wait-cursor': loading,
          selectedCursor,
          'with-street-view': showStreetView,
        }"
        :type="isDarkMap ? 'dark' : 'light'"
        :streetview="showStreetView ? 'enabled' : 'disabled'"
        @contextmenu.prevent=""
      >
        <div
          style="
            position: absolute;
            top: 7px;
            left: 0;
            width: 100%;
            z-index: 100;
          "
        >
          <v-progress-linear
            v-if="loading"
            :indeterminate="true"
            color="accent"
            background-color="white"
            background-opacity="0.7"
            height="7"
            style="margin-top: 0"
          />
          <v-progress-linear
            v-else
            color="white"
            background-color="white"
            background-opacity="0"
            class="elevation-1"
            height="2"
            style="margin-top: 0"
          />
        </div>

        <map-navigation-elements
          :show-street-view="showStreetView"
          @toggleStreetView="toggleStreetView"
          @centerWorkspace="centerOnWorkspace"
          @zoomIn="changeZoom(1)"
          @zoomOut="changeZoom(-1)"
          @forceWorkspaceFilter="setForceWorkspaceFilter(!forceWorkspaceFilter)"
        />

        <multi-object-form
          ref="multiObjectForm"
          :nextForm="nextForm"
          :creating-bulk-tp="bulk_tp.creating"
          :bulk-tp-coordinates="techPointsCoordinates"
          :blockage-object="newBlockage"
          @update-geoserver="updateGeoserver"
          @create-technical-local="startTechnicalLocalCreation"
          @clear-next-form="nextForm = null"
          @open-cables-split-dialog="openCablesSplitDialog($event)"
          @openDetails="openDetailFormFromObjectMenu"
          @refresh="refresh"
          @delete-measurement-area="deleteMeasurementArea"
          @update-tech-point-data="bulk_tp.techPointData = $event"
          @update-pathway-data="bulk_tp.pathwayData = $event"
        />

        <multi-selection-form
          v-if="multiSelectionOpen"
          @closeForm="
            multiSelectionOpen = false;
            drawLayerSource.clear();
          "
        />

        <work-order-form
          v-if="workorders.workOrderOpen"
          :workOrder_uuid="workorders.workorder_uuid"
          :actionType="workorders.actionType"
          @update-geoserver="updateGeoserver"
          @closeForm="closeForm"
        />

        <object-list
          v-if="objectList.open"
          :coordinates="contextMenuPixel"
          :objectList="objectList.list"
          @selectObject="getObjectDataFromList"
          @remove-highlight-from-menu="removeHighlightFromObjectOnLeave"
          v-on-clickaway="closeObjectList"
        />

        <object-menu
          v-if="objectMenu.open"
          :coordinates="contextMenuPixel"
          @updateGeoServer="updateGeoserver"
          @closeMenu="closeObjectMenu"
          @openForm="openFormFromObjectMenu"
          @openDetailForm="openDetailFormFromObjectMenu"
          @moveObject="moveObject"
          @update-loading="loading = $event"
          @openMultiSelectForm="prepareDataToSelectMultipleObjects"
          @openChangeLogForm="openChangeLogForm"
          @openWorkorderForm="openWorkorderForm"
          @openJoinCablesMenu="openJoinCablesMenu"
          @reverseCableDirection="reverseDirection"
          @redrawCluster="openRedrawClusterDialog($event)"
        />

        <join-cable-menu
          v-if="joinCableMenu.open"
          :coordinates="contextMenuPixel"
          :connections="joinCableMenu.connections"
          :cable-uuid="joinCableMenu.cableUuid"
          @closeMenu="closeJoinCableMenu"
          v-on-clickaway="closeJoinCableMenu"
        />

        <measurements-toolbar
          :vectorSource="measurementsLayerSource"
          @clear="clearMeasurements"
          v-if="showMeasurementsToolbar"
        />

        <create-objects-toolbar
          v-if="showObjectToolbar"
          @enableCableCreation="enableCableCreation"
          @enablePathwayCreation="enableObjectCreation"
          @enableClusterCreation="enableClusterCreation"
          @enableClusterToolCreation="enableClusterToolCreation"
          @enableAreaCreation="enableObjectCreation"
          @enableBulkTPCreation="enableBulkTPCreation"
          @enableBundleCreation="enableBundleCreation"
          @enableExternalImport="enableExternalImport"
          @resetLineString="resetLineString"
          @resetArea="resetArea(true)"
          @resetSelectedObjectType="resetSelectedObjectLayer"
        />

        <cable-window
          v-if="cable.creating"
          ref="cableWindow"
          :cable="cable"
          :filter-pathways-with-ignore="cable.filterPathwaysWithIgnore"
          :lineStringWKT="lineStringWKT"
          :vectorSource="drawLayerSource"
          :position="cable.position"
          @clear="resetCable"
          @close="closeCable"
          @openForm="openForm"
          @add-node="getNodeWKT"
          @updateGeoserver="updateGeoserver"
          @filterPathwaysWithIgnore="cable.filterPathwaysWithIgnore = $event"
        />

        <cluster-window
          v-if="cluster.creating"
          ref="clusterWindow"
          :cluster="cluster"
          :clusterWKT="clusterWKT"
          :vectorSource="drawLayerSource"
          :position="cluster.position"
          @resetCluster="resetCluster"
          @close="closeCluster"
          @updateGeoserver="updateGeoserver"
          @drawNewPolygon="drawNewPolygon"
          @drawNewPoint="getNodeWKT"
          @openForm="openForm"
        />
        <cluster-tool-window
          v-if="clusterTool.creating"
          :vectorSource="drawLayerSource"
          :position="clusterTool.position"
          @close="closeClusterTool"
          @drawNewPolygon="drawNewPolygon"
          :clusterTool="clusterTool"
          :clusterWKT="clusterToolWKT"
          @resetClusterTool="resetClusterTool"
          @updateGeoserver="updateGeoserver"
          @openForm="openForm"
        />

        <bulk-tp-window
          v-if="bulk_tp.creating"
          ref="bulkTpWindow"
          :bulk-t-p="bulk_tp"
          :position="bulk_tp.position"
          :too-many-points-drawn="bulk_tp.tooManyPointsDrawn"
          :bulk-feature="!!bulkFeature"
          @resetBulkTP="resetBulkTP"
          @close="closeBulkTP"
          @adjustParameters="adjustParameters($event)"
          @openTechPointSelectionDialog="bulk_tp.dialog = true"
          :count="techPointsCoordinates.length"
        />

        <bundle-window
          v-if="bundle.creating"
          ref="bundleWindow"
          :position="bundle.position"
          :bundle="bundle"
          :lineStringWKT="lineStringWKT"
          :vectorSource="drawLayerSource"
          @close="closeBundle"
          @clear="resetCable"
          @createBundle="openBundleForm"
          @add-node="getNodeWKT"
          @updateGeoserver="updateGeoserver"
          @openForm="openForm"
        />

        <external-import-window
          v-if="externalImport.open"
          ref="externalImportWindow"
          @close="closeExternalImportWindow"
          @importExternalData="
            importExternalData($event.layers, $event.service)
          "
        />

        <processing-window
          v-if="currentActionType === 'processing'"
          :position="processingWindow.position"
          @update-geoserver="updateGeoserver"
        />

        <NetworkLayers v-if="showNetworkLayers" />

        <path-inspector-toolbar
          v-show="currentActionType === 'pathInspector'"
          :vectorSource="drawLayerSource"
          :position="pathInspectorPosition"
          @updateGeoserver="updateGeoserver"
          @close="closePathInspector"
          @clear="resetPathInspector"
          @openForm="openForm"
        />

        <fiber-circuit-window
          v-show="fiberCircuit.open"
          :fiber-uuid="fiberCircuit.uuid"
          @close="closeFiberCircuit"
          :position="fiberCircuit.position"
        />

        <heatmap-toolbar
          v-if="showHeatmapToolbar"
          @generate-heatmap="generateHeatmap($event)"
          @clear-heatmap="heatmapLayerSource.clear()"
        />
      </div>

      <!-- Section with Satellite and Street View - usually hidden -->
      <div v-show="showStreetView" id="streetViewContainer">
        <div id="google-map" />
        <div id="street-view" />
      </div>

      <pathway-split-pathway-dialog
        :open="linkPathwayToPathway.dialog"
        :on-first-point="linkPathwayToPathway.onFirstPoint"
        @cancel="cancelConnectCurrentPathwayWithPathway"
        @split="splitOtherPathwayWithCurrentPathway"
        @snap="snapCurrentPathwayToOtherPathwayTip"
      />

      <dialog-tech-point-split-or-snap-pathway
        :open="linkTechPointToPathwayDialog"
        @split="respondToObjectSplitPathway(true)"
        @snap="snapTechPointToPathway"
        @cancel="cancelConnectTechPointToPathway"
      />

      <dialog-closure-without-technical-point
        :open="closureWithoutTechPoint.dialog"
        @mainAction="startClosureCreation"
        @secondaryAction="closureWithoutTechPoint.dialog = false"
      />

      <app-dialog-box
        :open="openDialogBoxActiveLayer"
        :title="$t('LayerIsDeactivated')"
        :content="$t('ActivateLayer')"
        :main-action-label="$t('Yes')"
        @mainAction="activateLayer"
        :secondary-action-label="$t('Cancel')"
        @secondaryAction="openDialogBoxActiveLayer = false"
      />

      <dialog-split-cables-creating-closure
        v-if="cablesSplitDialog.open && technicalPointData"
        :open="cablesSplitDialog.open"
        :tp-uuid="technicalPointData.tp_uuid"
        @secondaryAction="closeCablesSplitDialog"
        @mainAction="confirmCablesSplit($event)"
      />

      <dialog-bulk-tech-point-type
        :open="bulk_tp.dialog"
        @selectedTechPoint="openSelectedTechPointForm($event)"
        @cancelCreation="cancelBulkTPCreation"
      />

      <app-dialog-box
        :open="externalImport.successful"
        :title="$tc('ImportNoun', 1)"
        :content="$t('SuccessfulExternalImportMessage')"
        :main-action-label="$t('Cancel')"
        @mainAction="cancelRedirect"
        :secondary-action-label="$t('module_project_management')"
        @secondaryAction="redirectToWorkspaceManagement"
      />

      <app-dialog-box
        :open="redrawClusterTile.open"
        :title="$t('RedrawCluster')"
        :content="$t('RedrawClusterWarning')"
        :main-action-label="$t('Cancel')"
        @mainAction="closeRedrawWindow"
        :secondary-action-label="$t('RedrawCluster')"
        @secondaryAction="redrawCluster()"
        :loading="loading"
      />

      <app-dialog-box
        :open="continueInvertCables.open"
        :title="$t('InvertCableConfirmation')"
        :content="$t('InvertCableMessage')"
        :main-action-label="$t('Cancel')"
        @mainAction="closeInvertCablesWindow"
        :secondary-action-label="$t('Invert')"
        @secondaryAction="invertCables"
      />
    </div>
  </div>
</template>

<script>
import { bus } from "@/main.js";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { layersMixin } from "@/mixins/layersMixin";
import { mixin as clickaway } from "vue-clickaway";
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";

import { bearingToAzimuth, booleanContains, feature, point } from "@turf/turf";
import { toJpeg } from "html-to-image";
import jsPDF from "jspdf";

/* OpenLayers Imports */
import { Collection, Feature, Map, Overlay, View } from "ol";
import {
  BingMaps,
  ImageWMS,
  OSM,
  Vector as VectorSource,
  XYZ,
} from "ol/source.js";
import {
  fromLonLat,
  get as getProjection,
  METERS_PER_UNIT,
  toLonLat,
  transform,
  transformExtent,
} from "ol/proj";
import { register } from "ol/proj/proj4";
import proj4 from "proj4";
import { boundingExtent } from "ol/extent";
import {
  defaults as defaultControls,
  MousePosition,
  OverviewMap,
} from "ol/control.js";
import {
  Image as ImageLayer,
  Tile as TileLayer,
  Vector as VectorLayer,
  Heatmap as HeatmapLayer,
} from "ol/layer.js";
import { GeoJSON, WKT } from "ol/format";
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from "ol/style.js";
import { LineString, Point, Polygon } from "ol/geom.js";
import { defaults, Draw, Modify } from "ol/interaction.js";
import { format } from "ol/coordinate";
import { unByKey } from "ol/Observable";
import { getArea, getLength } from "ol/sphere";

import html2canvas from "html2canvas";

import MultiObjectForm from "../Forms/MultiObjectForm";
import ObjectList from "./ObjectList/ObjectList";
import ObjectMenu from "./ObjectMenu/ObjectMenu";
import WindowObjectMenu from "./ObjectMenu/WindowObjectMenu";
import CreateObjectsToolbar from "../Toolbars/CreateObjects/CreateObjectsToolbar";
import MultiSelectionForm from "../Forms/MultiSelectionForm/MultiSelectionForm";
import MapNavigationElements from "@/components/Home/Content/NavigationElements";
import MeasurementsToolbar from "@/components/Home/Toolbars/MeasurementsToolbar";
import ClusterWindow from "./ObjectWindow/Cluster/ClusterWindow";
import CableWindow from "./ObjectWindow/CableWindow";
import ProcessingWindow from "@/components/Home/Content/ObjectWindow/Processing/ProcessingWindow";
import WorkOrderForm from "../Forms/WorkOrderForm/WorkOrderForm.vue";
import AppDialogBox from "../../_Molecules/AppDialogBox";
import LoadingSpinner from "vue-loading-overlay";
import utils from "@/utils";
import PathwaySplitPathwayDialog from "@/components/Home/Content/Dialog/DialogPathwaySplitPathway";
import NetworkLayers from "@/components/Home/SideBar/SidebarWindows/Layers/NetworkLayers";
import PathInspectorToolbar from "@/components/Home/Toolbars/PathInspectorToolbar";
import DialogClosureWithoutTechnicalPoint from "@/components/Home/Content/Dialog/DialogClosureWithoutTechnicalPoint";
import FiberCircuitWindow from "@/components/Home/Content/ObjectWindow/FiberCircuit/FiberCircuitWindow";
import JoinCableMenu from "@/components/Home/Content/ObjectMenu/JoinCableMenu/JoinCableMenu";
import DialogTechPointSplitOrSnapPathway from "@/components/Home/Content/Dialog/DialogTechPointSplitOrSnapPathway";
import DialogSplitCablesCreatingClosure from "@/components/Home/Content/Dialog/DialogSplitCablesCreatingClosure";
import DialogBulkTechPointType from "@/components/Home/Content/Dialog/DialogBulkTechPointType";
import HeatmapToolbar from "@/components/Home/Toolbars/HeatmapToolbar";
import BulkTpWindow from "@/components/Home/Content/ObjectWindow/BulkTP/BulkTPWindow";
import endpoints from "@/utils/endpoints";
import ClusterToolWindow from "@/components/Home/Content/ObjectWindow/ClusterTool/ClusterToolWindow";
import BundleWindow from "@/components/Home/Content/ObjectWindow/Bundle/BundleWindow";
import ExternalImportWindow from "@/components/Home/Content/ObjectWindow/ExternalImport/ExternalImportWindow";
import axios from 'axios';

export default {
  name: "MapT",

  mixins: [clickaway, checkPermissionsMixin, layersMixin],

  components: {
    HeatmapToolbar,
    ClusterToolWindow,
    DialogSplitCablesCreatingClosure,
    DialogTechPointSplitOrSnapPathway,
    JoinCableMenu,
    FiberCircuitWindow,
    DialogClosureWithoutTechnicalPoint,
    PathInspectorToolbar,
    NetworkLayers,
    PathwaySplitPathwayDialog,
    ProcessingWindow,
    MeasurementsToolbar,
    MapNavigationElements,
    MultiSelectionForm,
    AppDialogBox,
    ObjectList,
    ObjectMenu,
    CreateObjectsToolbar,
    ClusterWindow,
    CableWindow,
    WindowObjectMenu,
    MultiObjectForm,
    LoadingSpinner,
    WorkOrderForm,
    BulkTpWindow,
    DialogBulkTechPointType,
    BundleWindow,
    ExternalImportWindow,
  },

  data() {
    return {
      mapEvent: null,
      loading: false,
      map: undefined,
      mapView: undefined,
      roadMapLayer: undefined,
      roadMapSource: undefined,
      aerialMapLayer: undefined,
      openStreetMapSource: undefined,
      streetViewPointerLayer: undefined,
      googleMap: undefined,
      panoramaMap: undefined,
      streetViewService: undefined,

      drawLayerSource: undefined,
      modifyInteraction: undefined,

      highlightObjectLayerSource: undefined,
      highlightLayerSource: undefined,
      measurementsLayerSource: undefined,
      heatmapLayerSource: undefined,

      showStreetView: false,

      isDarkMap: false,

      streetViewPointer: undefined,

      objectList: {
        list: [],
        open: false,
      },
      objectMenu: {
        open: false,
      },

      currentProjection: null,
      workorders: {
        workOrderOpen: false,
        workorder_uuid: null,
        actionType: null,
      },
      contextMenuPixel: [],
      contextMenuCoords: [],
      allLayers: [],

      singleClickEvent: null,
      clickType: "",
      mousePositionControl: null,

      wkt: null,
      lineStringWKT: [],

      technicalPointData: null,
      technicalSiteData: null,

      cable: {
        creating: false,
        creatable: false,
        tier: "2",
        pathways: [],
        position: {
          x: 1200,
          y: 0,
        },
        filterPathwaysWithIgnore: false,
      },

      clusterWKT: [],
      clusterToolWKT: [],
      cluster: {
        creating: false,
        creatable: false,
        type: null,
        position: {
          x: 1200,
          y: 0,
        },
      },
      clusterTool: {
        creating: false,
        creatable: false,
        type: null,
        position: {
          x: 1200,
          y: 0,
        },
      },

      bulk_tp: {
        creating: false,
        position: {
          x: 1200,
          y: 0,
        },
        numberOfTPs: 0,
        distanceBetweenTPs: 0,
        createPathway: null,
        dialog: false,
        techPointData: null,
        pathwayData: null,
        tooManyPointsDrawn: false,
      },
      bulkTPLineVerticesCoordinates: null,
      techPointsCoordinates: [],
      bulkFeature: null,
      bulkTechPointStyle: new Style({
        image: new CircleStyle({
          radius: 4,
          stroke: new Stroke({
            color: "#00a8f3",
            width: 2,
            zIndex: 1,
          }),
          fill: new Fill({
            color: "#00a8f3",
          }),
        }),
      }),

      linkPathwayToPathway: {
        dialog: false,
        snap: false,
        split: false,
        onFirstPoint: false,
        startPathwayData: null,
        endPathwayData: null,
      },

      linkObjectToPathway: {
        split: false,
        pathway: { uuid: null },
      },

      pathwayNodes: [],
      pathwayVerticesCoordinates: [],

      pathwayBeingMoved: {
        feature: null,
        coordinates: null,
        vertexBeingModified: null,
        startDisconnected: false,
        endDisconnected: false,
        originalData: null,
      },

      linkTechPointToPathwayDialog: false,

      newSlack: {
        cableUUID: null,
        nodeObject: null,
        possibleNodeObjects: [],
        openDialog: false,
      },

      newBlockage: {
        nodeObject: null,
        pathway_id: null,
        technical_point_id: null,
        openDialog: false,
      },

      fieldCableNodes: [],

      editingObjectType: null,

      multiSelectionOpen: false,

      closureWithoutTechPoint: {
        dialog: false,
        event: null,
        passingObjects: false,
        technicalPoint: null,
      },

      measurementsDraw: null,
      sketch: null,
      measureTooltipElement: null,
      measureTooltip: null,

      pathwayTooltipElement: null,
      pathwayTooltip: null,
      draw: null,

      newObjectDraw: null,

      moveObjectData: null,

      pathInspectorPosition: {
        x: 1200,
        y: 0,
      },

      checkedLayers: [],

      openDialogBoxActiveLayer: false,

      fiberCircuit: {
        open: false,
        uuid: null,
        highlightObjectLayerSource: undefined,
        position: {
          x: 1200,
          y: 0,
        },
      },

      processingWindow: {
        position: {
          x: 1200,
          y: 0,
        },
      },

      joinCableMenu: {
        open: false,
        connections: [],
        cableUuid: null,
        highlightObjectLayerSource: null,
      },

      cablesSplitDialog: {
        open: false,
      },

      nextForm: "",

      bundle: {
        creating: false,
        creatable: false,
        position: {
          x: 1200,
          y: 0,
        },
        pathways: [],
      },

      externalImport: {
        open: false,
        successful: false,
      },

      redrawClusterTile: {
        open: false,
        uuid: null,
      },

      continueInvertCables: {
        open: false,
        uuid: null,
      },
      cancel: null,
      sourceCancel: null,

      minNumberHPsPerMDU: null,
    };
  },

  computed: {
    ...mapGetters({
      currentlyOpenSidebar: "getCurrentlyOpenHomeSidebar",
      toolsSidebarIsOpen: "isHomeToolsSidebarOpen",
    }),

    currentActionType() {
      return this.$store.getters.tools_selectedAction;
    },

    activeNetworkFilters() {
      return this.$store.getters.getActiveNetworkFilters;
    },

    mapStyle() {
      return this.$store.getters.getCurrentMainMapStyle;
    },

    selectedCursor() {
      let tool = this.selectedToolObjectLayer;
      if (tool) {
        return tool.toLowerCase() + "-cursor";
      } else {
        return "default-cursor";
      }
    },

    selectedToolObjectLayer() {
      return this.$store.getters.tools_selectedObjectLayer;
    },

    selectedMeasurementTool() {
      return this.$store.getters.getSelectedMeasurementTool;
    },

    showObjectToolbar() {
      return this.$store.getters.isObjectToolbarOpen;
    },

    selectedObjectIdentifiers() {
      return this.$store.getters.selectedObjectIdentifiers;
    },

    selectedProjection() {
      return "EPSG:" + this.$store.getters.getSelectedProjection;
    },

    objectToZoomGeoJson() {
      return this.$store.getters.getObjectToZoomGeoJson;
    },

    showMeasurementsToolbar() {
      return this.$store.getters.isMeasurementsToolbarOpen;
    },

    forceWorkspaceFilter() {
      if (this.isAdmin || this.isSuperAdmin) {
        return this.$store.getters.getForceWorkspaceFilter;
      } else {
        return "true";
      }
    },

    showNetworkLayers() {
      return this.$store.getters.isShowTabNetworkLayers;
    },

    isToTakePrint() {
      return !!this.$store.getters.isToTakePrint;
    },

    /**
     * Size of screen
     * @returns {number}
     */
    screenSize() {
      return !!this.$refs.map ? this.$refs.map.offsetWidth : null;
    },

    /** defines whether the heatmap tool is selected or not
     * @returns {any}
     */
    showHeatmapToolbar() {
      return this.$store.getters.isShowHeatmap;
    },

    objectNetworkCenterOnWorkspace() {
      return this.$store.getters.getGeomObjectNetworkToCenter;
    },
  },

  watch: {
    activeNetworkFilters() {
      this.updateGeoserverLayers();
    },

    mapStyle(value) {
      this.changeLocalMapStyle(value);
    },

    objectToZoomGeoJson(objectGeoJSON) {
      this.removeHighlightFromObjectOnLeave();
      if (objectGeoJSON) {
        this.centerMapOnObject(objectGeoJSON);
      }
    },

    showMeasurementsToolbar(show) {
      if (!show) {
        this.clearMeasurements();
      }
    },

    currentActionType(newValue) {
      if (newValue === "" && this.selectedMeasurementTool !== "") {
        this.disableMapClickEvents();
      } else {
        this.map.removeInteraction(this.newObjectDraw);
        if (newValue === "select") {
          this.resetMapClickEvents();
        }
      }
    },

    selectedMeasurementTool(selectedTool) {
      this.map.removeInteraction(this.measurementsDraw);
      if (selectedTool !== "") {
        this.addMeasurementInteraction();
      } else {
        this.enableMapClickEvents();
      }
    },

    forceWorkspaceFilter() {
      this.objectsImageSource.setImageLoadFunction((image, src) =>
        this.loadGeoserverObjectsImage(image, src)
      );
    },

    showStreetView() {
      this.setInitialPositionWindows();
    },

    isToTakePrint(value) {
      if (value) {
        this.getScreenShotMap();
      }
    },

    screenSize() {
      this.setInitialPositionWindows();
    },

    showHeatmapToolbar(value) {
      if (!value) {
        this.heatmapLayerSource.clear();
      }
    },

    "bulk_tp.techPointData"() {
      if (!this.bulk_tp.createPathway && this.bulk_tp.techPointData) {
        this.createBulkTechPoints();
      }
    },

    "bulk_tp.pathwayData"() {
      if (this.bulk_tp.techPointData && this.bulk_tp.pathwayData) {
        this.createBulkTechPoints();
      }
    },
  },

  mounted() {
    const CancelToken = axios.CancelToken;
    this.sourceCancel = CancelToken.source();

    if (this.userHasFibercloudPermission("list_list")) {
      this.GET_ALL_LISTS();
    }
    //update data of sessions storage and store state
    this.getMapInfo();
    this.initializeMap();
    this.initializeMarkPoint();
    this.initializeStreetView();
    this.adjustMapControls();
    this.registerProjections();
    this.createModifyInteraction();
    this.getAllLayers();

    // Listener that saves the map position on move end
    this.map.on("moveend", () => this.updateStoredMapData());

    this.$nextTick(() => {
      bus.$emit("update-home-footer-map-scale", this.getScale());
    });

    this.mapView.on("change:resolution", () => {
      bus.$emit("update-home-footer-map-scale", this.getScale());
    });

    bus.$on("change-home-map-scale", (scale) => {
      this.changeScale(scale);
    });

    bus.$on("change-home-map-coordinate", (coordinates) => {
      this.centerMapFromCoordinatesForm(coordinates);
    });

    bus.$on("reset-closure-without-TP", () => {
      this.closureWithoutTechPoint.passingObjects = true;
      this.closureWithoutTechPoint.technicalPoint = null;
    })

    bus.$on("open-floorbox", () => {
      this.openFloorboxForm();
    });

    this.enableMapClickEvents();

    /**
     * Checks if the route has the parameters to focus on one spot. If it doesn't, it centers the map in the middle of the workspace
     * Missing loading the workspace to cache, like it's being done in the WorkspacesList
     */
    if (
      this.$route.params.lat &&
      this.$route.params.long &&
      this.$route.params.zoom
    ) {
      this.mapView.setZoom(this.$route.params.zoom);
      this.centerMap(this.$route.params.lat, this.$route.params.long);
    } else if (this.$route.params.objectGeoJSON) {
      this.centerMapOnObject(this.$route.params.objectGeoJSON)
    } else {
      this.setInitialSavedPosition();
    }

    this.setSavedMapType();

    window.addEventListener("keyup", this.escKeyAction.bind(this));

    bus.$on("enable-click-events", () => this.resetMapClickEvents());

    bus.$on("reset-line-string", () => this.resetLineString());
    bus.$on("reset-area", () => this.resetArea(true));
    bus.$on("reset-pathway-and-split-data", () => this.resetPathwayData());
    bus.$on("reset-slack-data", () => this.resetSlackData());
    bus.$on("end-cable-creation", () => this.endCableCreation());
    bus.$on("reset-blockage-data", () => this.resetBlockageData());

    bus.$on("close-object-select-form", () => {
      this.multiSelectionOpen = false;
      this.finishModifyingObject();
    });

    bus.$on("highlight-object-from-menu", (geoJSON) =>
      this.highlightObjectOnHover(geoJSON)
    );
    bus.$on("remove-highlight-from-menu", () =>
      this.removeHighlightFromObjectOnLeave()
    );
    bus.$on("remove-measurement-area-highlight-from-menu", (object) => {
      this.removeMeasurementAreaHighlightFromObjectOnLeave(object);
    });

    bus.$on("highlight-object-from-selected-table", (data) =>
      this.highlightObjectSelected(data)
    );
    bus.$on("remove-highlight-from-selected-table", (uuid) => {
      if (uuid) {
        this.removeHighlightFromSelectObject(uuid);
      } else {
        this.highlightObjectLayerSource.clear();
      }
    });

    bus.$on("update-map-size", () => {
      if (this.map) {
        this.map.updateSize();
      }
    });

    bus.$on("update-geoserver", () => this.updateGeoserver());
    bus.$on("update-geoserver-layers", () => this.updateGeoserverLayers());

    bus.$on("update-projection", (projection) => {
      this.updateProjection(projection);
      this.currentProjection = projection;
    });

    bus.$on("open-object-form", (data) =>
      this.openForm(data.layer, data.action)
    );

    //Fiber Circuit
    bus.$on("show-fiber-circuit", (data) => this.openFiberCircuit(data));

    bus.$on("highlight-object-from-fiber-circuit-selected", (data) =>
      this.highlightObjectFiberCircuitSelected(data)
    );
    bus.$on("remove-highlight-from-fiber-circuit-selected", () => {
      if (this.fiberCircuit.highlightObjectLayerSource) {
        this.fiberCircuit.highlightObjectLayerSource.clear();
      }
    });

    bus.$on("highlight-join-cable-menu", (data) =>
      this.highlightObjectJoinCablesMenu(data)
    );
    bus.$on("remove-highlight-join-cable-menu", () => {
      if (this.fiberCircuit.highlightObjectLayerSource) {
        this.removeHighlightObjectJoinCablesMenu();
      }
    });

    bus.$on("change-view-type", (type) => this.changeViewType(type));

    bus.$on("end-bundle-creation", (type) => this.endBundleCreation());

    window.addEventListener("resize", this.setInitialPositionWindows);

    this.$nextTick(() => {
      if (this.objectNetworkCenterOnWorkspace) {
        this.SET_OBJECT_TO_ZOOM_ON_MAP(this.objectNetworkCenterOnWorkspace);
        this.centerObjectNetworkOnWorkspace(null);
      }
    });

    this.getNetworkRuleMDU();
  },

  beforeDestroy() {
    this.sourceCancel.cancel('Operation canceled by the user.');
    window.removeEventListener("resize", this.setInitialPositionWindows);
  },

  destroyed() {
    bus.$off("reset-closure-without-TP")
    bus.$off("enable-click-events");
    bus.$off("reset-line-string");
    bus.$off("reset-area");
    bus.$off("reset-pathway-and-split-data");
    bus.$off("reset-slack-data");
    bus.$off("end-cable-creation");
    bus.$off("highlight-object-from-menu");
    bus.$off("remove-highlight-from-menu");
    bus.$off("highlight-object-from-selected-table");
    bus.$off("remove-highlight-from-selected-table");
    bus.$off("update-map-size");
    bus.$off("update-geoserver");
    bus.$off("update-geoserver-layers");
    bus.$off("update-projection");
    bus.$off("update-object-form-position");
    bus.$off("change-home-map-scale");
    bus.$off("change-home-map-coordinates");
    bus.$off("close-object-select-form");
    bus.$off("open-object-form");
    bus.$off("show-fiber-circuit");
    bus.$off("highlight-object-from-fiber-circuit-selected");
    bus.$off("remove-highlight-from-fiber-circuit-selected");
    bus.$off("highlight-join-cable-menu");
    bus.$off("remove-highlight-join-cable-menu");
    bus.$off("change-view-type");
    bus.$off("end-bundle-creation");
    bus.$off("reset-blockage-data");
  },

  methods: {
    ...mapActions([
      "GET_ALL_LISTS",
      "fetchImageFromGeoServer",
      "fetchFeaturesFromGeoServer",
      "EDIT_OBJECTS",
      "fetchWorkspaceByUUID",
      "CREATE_CLUSTER",
      "GET_OBJECTS_LAYERS_INSIDE",
      "SET_OBJECT_TO_ZOOM_ON_MAP",
      "SET_TOOL_ACTION",
      "SET_TOOL_OBJECT_LAYER",
      "SET_TOOL_TECHNICAL_TYPE",
      "UPDATE_SELECTED_OBJECT_IDENTIFIERS",
      "setSelectedMeasureTool",
      "setMeasurementsToolbarState",
      "fetchObjectByUUID",
      "setOpenHomeSidebar",
      "setHomeToolsSidebarState",
      "fetchSlackNearestObjects",
      "setValidAssetReservationObjectLayers",
      "UPDATE_SELECTED_OBJECT_DATA",
      "setHomePrintState",
      "fetchConnectionsToJoinCables",
      "reverseCableDirection",
      "fetchCablesPassingInTechPoint",
      "setForceWorkspaceFilter",
      "GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON",
      "centerObjectNetworkOnWorkspace",
      "createBulkTPs",
      "createNewImport",
      "setCurrentWorkspace",
      "fetchWorkspaceByUUID",
      "RESET_NETWORK_DIAGRAM_STATE",
      "callRedrawCluster",
      "getLayersMenu",
      "FETCH_ALL_NETWORK_RULES_TEMPLATES",
    ]),

    typeChecker(tpType){
      switch (tpType) {
        case "C":
          return "fc_manhole"

        case "V":
          return "fc_virtual"

        case "A":
          return "fc_pole"

        case "B":
          return "fc_cabinet"

        default:
          break;
      }
    },

    ///////////////////////////
    /* Click events' methods */
    ///////////////////////////

    mapClickListener() {
      unByKey(this.singleClickEvent);
      unByKey(this.clickEvent);
      this.clickEvent = this.map.on("click", (e) => {
        this.closeObjectMenus();
        this.singleClickAction(e);
      });
    },

    doubleClickListener() {
      unByKey(this.doubleClickEvent);
      this.doubleClickEvent = this.map.on("dblclick", (e) => {
        this.closeObjectMenus();
        this.doubleClickAction(e);
      });
    },

    rightClickListener() {
      unByKey(this.rightClickEvent);
      this.rightClickEvent = this.map.on("contextmenu", (e) =>
        this.debounceRightClick(e)
      );
    },

    debounceRightClick: _.debounce(function (e) {
      e.preventDefault();
      this.closeObjectMenus();
      this.clickType = "rightClick";
      this.getObjects(e);
    }, 500),

    singleClickAction(e) {
      this.linkObjectToPathway.split = false;
      this.clickType = "leftClick";
      this.contextMenuPixel = e.pixel;
      this.contextMenuCoords = e.coordinate;
      switch (this.currentActionType) {
        case "create":
        case "createAssetReservation":
          if (this.isClickInsideWorkspace(e)) {
            let objectType = this.selectedToolObjectLayer;
            if (objectType !== "") {
              if (
                this.isLayerActive(objectType) ||
                objectType === "fc_bulk_tp" ||
                objectType === "fc_cluster_tool" ||
                objectType === "external_import"
              ) {
                this.createObject(e, objectType);
              } else {
                this.openDialogBoxActiveLayer = true;
              }
            }
          }
          break;

        case "select":
          if (!this.multiSelectionOpen) {
            this.startSelectionMultipleObjects(e);
          }
          break;
        case "workOrder":
          if (this.isLayerActive("field_workorders")) {
            if (!this.workorders.workOrderOpen) {
              this.startSelectionMultipleObjects(e);
            }
          } else {
            this.openDialogBoxActiveLayer = true;
          }

          break;

        case "pathInspector":
          if (this.isClickInsideWorkspace(e)) {
            this.startPathInspector();
          }
          break;

        case "addAssetsToReservation":
          if (this.isClickInsideWorkspace(e)) {
            let validLayers =
              this.$store.getters.getValidAssetReservationAssetLayers;
            this.addObjectsToReservation(e, validLayers);
          }
          break;

        case "addNetworkElementsToReservation":
          if (this.isClickInsideWorkspace(e)) {
            let validLayers =
              this.$store.getters.getValidAssetReservationNetworkElementLayers;
            this.addObjectsToReservation(e, validLayers);
          }
      }
    },

    doubleClickAction(e) {
      this.clickType = "leftClick";
      if (!this.selectedToolObjectLayer) {
        this.getObjects(e, this.selectedToolObjectLayer);
      }
    },

    enableMapClickEvents() {
      this.mapClickListener();
      this.doubleClickListener();
      this.rightClickListener();
    },

    disableMapClickEvents() {
      unByKey(this.clickEvent);
      unByKey(this.rightClickEvent);
      unByKey(this.doubleClickEvent);
    },

    resetMapClickEvents() {
      this.disableMapClickEvents();
      this.enableMapClickEvents();
    },

    /////////////////////////////
    /* Map preparation methods */
    /////////////////////////////

    initializeMap() {
      this.mapView = this.createMapView();
      this.roadMapSource = this.createRoadMapSource();
      this.openStreetMapSource = this.createOpenStreetMapSource();
      this.roadMapLayer = this.createRoadMapLayer();
      this.aerialMapLayer = this.createAerialMapLayer();
      let objectsImageLayer = this.createObjectsImageLayer();
      let drawLayer = this.createDrawLayer();
      let highlightLayer = this.createHighlightLayer();
      let highlightObjectSelectedLayer =
        this.createHighlightObjectSelectLayer();
      let highlightObjectFiberCircuitLayer =
        this.createHighlightObjectFiberCircuitLayer();
      let highlightObjectJoinCablesLayer =
        this.createHighlightObjectJoinCablesLayer();
      let measurementsLayer = this.createMeasurementsLayer();
      let overviewMap = this.createOverviewMap();
      let heatmapLayer = this.createHeatMap();
      this.mousePositionControl =
        this.calculateCursorPositionCoordinatesOnMap();

      this.map = new Map({
        target: "map",
        view: this.mapView,
        layers: [
          this.roadMapLayer,
          this.aerialMapLayer,
          objectsImageLayer,
          drawLayer,
          highlightLayer,
          highlightObjectSelectedLayer,
          highlightObjectFiberCircuitLayer,
          highlightObjectJoinCablesLayer,
          measurementsLayer,
          heatmapLayer,
        ],

        interactions: defaults({
          doubleClickZoom: false,
          mouseWheelZoom: true,
        }),

        controls: defaultControls().extend([
          this.mousePositionControl,
          overviewMap,
        ]),
      });

      this.getActiveLayerMap();
    },
    async getMapInfo() {
      const workspace = await this.fetchWorkspaceByUUID({
        uuid: this.$route.params.workspace_uuid,
        includes: "engineering_rule",
        fields: "wp_uuid,wp_label,wp_id,wp_geo_json,wp_engineering_rule_id",
      });
      this.setCurrentWorkspace(workspace);
    },
    createMapView() {
      return new View({
        center: fromLonLat([0, 45]),
        zoom: 4,
        minZoom: 3,
        maxZoom: 25,
      });
    },

    createRoadMapLayer() {
      return new TileLayer({
        title: "roadMapLayer",
        visible: false,
        source: this.roadMapSource,
      });
    },
    createRoadMapSource() {
      return new XYZ({
        url: "https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
        crossOrigin: "Anonymous",
      });
    },
    createAerialMapLayer() {
      return new TileLayer({
        title: "aerialMapLayer",
        visible: false,
        source: new BingMaps({
          key: "AtUEuM0GKaXtnijUSNcuIcmQItWcpMstzANuR3ONhBKeY3LsFPqLFznuwXMHP2kb",
          imagerySet: "Aerial",
          culture: "en-EN",
          labelOverlay: "visible",
          maxZoom: 19,
        }),
      });
    },

    createOpenStreetMapSource() {
      return new OSM();
    },

    createDrawLayer() {
      this.drawLayerSource = new VectorSource({ wrapX: false });
      return new VectorLayer({
        source: this.drawLayerSource,
      });
    },

    createHighlightLayer() {
      this.highlightLayerSource = new VectorSource({ wrapX: false });
      return new VectorLayer({
        source: this.highlightLayerSource,
      });
    },

    createMeasurementsLayer() {
      this.measurementsLayerSource = new VectorSource();
      return new VectorLayer({
        source: this.measurementsLayerSource,
        style: new Style({
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
          }),
          stroke: new Stroke({
            color: "#ffcc33",
            width: 2,
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: "#ffcc33",
            }),
          }),
        }),
      });
    },

    createOverviewMap() {
      return new OverviewMap({
        collapsible: false,
        autoPan: true,
        render: "",
        layers: [
          new TileLayer({
            source: new OSM({
              url: "https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            }),
          }),
        ],
      });
    },

    createObjectsImageLayer() {
      this.objectsImageSource = new ImageWMS({
        url: endpoints.FIBER_API_URL,
        params: {
          LAYERS: this.getActiveLayers(),
          viewparams: this.getViewParameters(),
          VERSION: "1.1.1",
          SRS: "EPSG:3857",
        },
        serverType: "geoserver",
        crossOrigin: "anonymous",
        ratio: 1,
        imageLoadFunction: (image, src) =>
          this.loadGeoserverObjectsImage(image, src),
      });

      return new ImageLayer({
        tileOptions: { crossOrigin: "anonymous" },
        source: this.objectsImageSource,
      });
    },

    getActiveLayers() {
      this.checkedLayers = JSON.parse(sessionStorage.getItem("active_layers"));
      if (!this.checkedLayers) {
        this.checkedLayers = [];
      }

      let activeLayersString = "";

      if (this.checkedLayers.length > 0) {
        let activeLayers = [];
        for (const layerLevel of this.checkedLayers) {
          if (layerLevel && layerLevel.hasOwnProperty("layers")) {
            for (const filter of layerLevel.layers) {
              if (filter.layer && filter.layer.layer) {
                let layerName = filter.layer.layer;

                let geoserverLabel =
                  process.env.GEOSERVER_WORKSPACE + ":" + layerName;

                if (
                  !activeLayers.find(
                    (item) => item.geoserver_label === geoserverLabel
                  )
                ) {
                  activeLayers.push({
                    weight: layerLevel.weight,
                    geoserver_label: geoserverLabel,
                  });
                }
              }
            }
          }
        }
        activeLayersString = this.getActiveLayersAsString(activeLayers);
      }
      return activeLayersString;
    },

    getActiveLayersAsString(activeLayers) {
      let activeLayersString = "";

      let orderedLayers = activeLayers.sort((a, b) =>
        a.weight > b.weight ? 1 : b.weight > a.weight ? -1 : 0
      );
      orderedLayers = orderedLayers.map((layer) => layer.geoserver_label);
      activeLayersString = orderedLayers.join(",");
      return activeLayersString;
    },

    getViewParameters() {
      let viewParams = "";
      if (this.checkedLayers) {
        viewParams += this.getNormalLayersFilters();
      }

      if (this.activeNetworkFilters && this.showNetworkLayers) {
        viewParams += this.getActiveNetworkFilters();
      }

      viewParams += this.getActiveGeoserverLabels();

      return viewParams;
    },

    getNormalLayersFilters() {
      let viewParams = [];

      let filters = {};
      if (this.checkedLayers) {
        for (const layerLevel of this.checkedLayers) {
          if (layerLevel.hasOwnProperty("layers")) {
            for (const filter of layerLevel.layers) {
              if (filter.var_name) {
                if (!filters.hasOwnProperty(filter.var_name)) {
                  filters[filter.var_name] = [];
                }
                filters[filter.var_name].push("'" + filter.value + "'");
              }
            }
          }
        }

        for (const [var_name, arrayOfValues] of Object.entries(filters)) {
          viewParams.push(var_name + ":" + arrayOfValues.join("\\,") + ";");
        }
      }

      return viewParams.join("");
    },

    getActiveNetworkFilters() {
      const networkLayersFilters = [
        "tp_owner",
        "cl_owner",
        "man_owner",
        "cb_logical_type",
      ];
      let viewParams = "";
      let fields = this.getFilterFields(this.activeNetworkFilters);
      for (let filter of networkLayersFilters) {
        if (fields.includes(filter)) {
          viewParams += this.getSubLayer(
            fields[fields.indexOf(filter)],
            this.activeNetworkFilters
          );
        } else {
          viewParams += `${filter}:'999'` + ";";
        }
      }
      return viewParams;
    },

    getFilterFields(list) {
      let allFields = [];
      if (list) {
        for (let layer of list) {
          if (layer.hasOwnProperty("filters")) {
            layer.filters.forEach((filter) => {
              allFields.push(filter.field);
            });
          }
        }
      }
      return [...new Set(allFields)];
    },

    getSubLayer(fieldName, list) {
      let viewParam = fieldName + ":";

      if (list) {
        for (let layer of list) {
          if (layer.hasOwnProperty("filters")) {
            let field = layer.filters.find(
              (filter) => filter.field === fieldName
            );
            if (field && field.hasOwnProperty("value")) {
              let value = "'" + field.value + "'\\,";
              if (!viewParam.includes(value)) {
                viewParam += value;
              }
            }
          }
        }
      }
      viewParam = viewParam.substring(0, viewParam.lastIndexOf("\\,"));
      return viewParam + ";";
    },

    getActiveGeoserverLabels() {
      let showLabelsParameter = "";
      let activeGeoserverLabels = JSON.parse(
        sessionStorage.getItem("active_geoserver_labels")
      );
      if (activeGeoserverLabels && activeGeoserverLabels.length > 0) {
        showLabelsParameter = "show_label:'";
        showLabelsParameter += activeGeoserverLabels.join("'\\,'");
        showLabelsParameter += "';";
      }
      return showLabelsParameter;
    },

    loadGeoserverObjectsImage(image, src) {
      let img = image.getImage();
      let defaultImgSrc =
        "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

        if (this.checkedLayers.length > 0) {
          this.loading = true;
          this.fetchImageFromGeoServer({
            params: this.getParams(src),
            workspaceUUID: this.$store.getters.workspace.uuid,
            token:this.sourceCancel.token,
            })
            .then((resp) => {
              src = this.convertImageFromGeoserver(resp);
              if (src) {
                img.src = src;
              } else {
                img.src = defaultImgSrc;
              }
            })
            .catch(() => {
              img.src = defaultImgSrc;
            })
            .then(() => {
              this.loading = false;
            });
        } else {
          img.src = defaultImgSrc;
      }
    },

    getParams(src) {
      let urlArray = src.split("?");
      let params = urlArray[1];
      params = params + "&scale=" + this.getScale();
      params = params + "&force_workspace_filter=" + this.forceWorkspaceFilter;
      return params;
    },

    convertImageFromGeoserver(resp) {
      let type = resp.headers["content-type"];

      if (type.indexOf("image") === 0) {
        let uInt8Array = new Uint8Array(resp.data);
        let charArray = [];
        for (let charCode of uInt8Array) {
          charArray.push(String.fromCharCode(charCode));
        }
        let imageData = charArray.join("");
        imageData = window.btoa(imageData);

        return "data:" + type + ";base64," + imageData;
      }
    },

    calculateCursorPositionCoordinatesOnMap() {
      document.getElementById("mouse-position").innerHTML = "";
      return new MousePosition({
        projection: "EPSG:4326",
        // comment the following two lines to have the mouse position be placed within the map.
        className: "custom-mouse-position",
        target: document.getElementById("mouse-position"),
        undefinedHTML: "",
        coordinateFormat: (coordinates) =>
          this.formatMouseCoordinates(coordinates),
      });
    },

    formatMouseCoordinates(coordinates) {
      let lat = coordinates[1];
      let lon = coordinates[0];

      lon = lon % 360;
      if (lon > 180) {
        lon = lon - 360;
      } else if (lon < -180) {
        lon = 360 + lon;
      }

      let modifiedCoordinate = [lon, lat];
      return format(modifiedCoordinate, "Lat: {y} Lon: {x}", 6);
    },

    adjustMapControls() {
      let zoom = this.map.controls.array_[0];
      let rotation = this.map.controls.array_[1];
      let attribution = this.map.controls.array_[2];
      this.map.removeControl(zoom);
      this.map.removeControl(attribution);
      this.map.removeControl(rotation);
    },

    updateStoredMapData() {
      let lastMapData = JSON.parse(sessionStorage.getItem("new_map_data"));
      sessionStorage.setItem("last_map_data", JSON.stringify(lastMapData));

      let newMapData = {
        workspace: this.$store.getters.workspace.uuid,
        position: this.mapView.calculateExtent(this.map.getSize()).join(";"),
      };
      sessionStorage.setItem("new_map_data", JSON.stringify(newMapData));
      let coordinates = transform(
        this.mapView.getCenter(),
        "EPSG:3857",
        "EPSG:4326"
      );
      this.updateURL(
        this.$route.params.workspace_uuid,
        coordinates[0].toFixed(6),
        coordinates[1].toFixed(6),
        this.mapView.getZoom()
      );
    },

    // Mark Point Feature
    initializeMarkPoint() {
      let iconFeature = new Feature({
        geometry: new Point([0, 0]),
        name: "Null Island",
        population: 4000,
        rainfall: 500,
      });

      let iconStyle = new Style({
        image: new Icon({
          anchor: [20, 40],
          anchorXUnits: "pixels",
          anchorYUnits: "pixels",
          opacity: 1,
          src: require("@/assets/map_icons/location.png"),
        }),
        zIndex: 2,
      });

      iconFeature.setStyle(iconStyle);
      this.markPointFeature = iconFeature;
    },

    /** Changes the zoom on the map with a 250ms animation duration **/
    changeZoom(zoomChange) {
      let previousZoom = this.mapView.getZoom();
      this.mapView.animate({
        zoom: previousZoom + zoomChange,
        duration: 250,
      });

      let coordinates = transform(
        this.mapView.getCenter(),
        "EPSG:3857",
        "EPSG:4326"
      );

      this.updateURL(
        this.$route.params.workspace_uuid,
        coordinates[0].toFixed(6),
        coordinates[1].toFixed(6),
        previousZoom + zoomChange
      );
    },

    /**
     * Active layer map selected
     */
    getActiveLayerMap() {
      let selectedLayer = JSON.parse(
        sessionStorage.getItem("active_layer_map")
      );

      if (selectedLayer) {
        switch (selectedLayer.translatable_label) {
          case "DefaultSatellite":
            this.changeViewType("aerialMapLayer");
            break;
          case "OpenStreetMap":
            this.$store.dispatch("SET_MAP_STYLE", null);
            this.changeViewType("openStreetMap");
            break;
          case "Dark":
            /** Emits an event to set the map to dark mode **/
            this.$store.dispatch("SET_MAP_STYLE", "dark-map");
            this.changeViewType("roadMapLayer");
            break;
          case "Light":
            /** Emits an event to set the map to light mode **/
            this.$store.dispatch("SET_MAP_STYLE", "light-map");
            this.changeViewType("roadMapLayer");
            break;
          default:
            this.changeViewType("newLayer");
            break;
        }
      }
    },

    /** Changes view type from selected layer map **/
    changeViewType(type) {
      let selectedLayer = JSON.parse(
        sessionStorage.getItem("active_layer_map")
      );
      this.roadMapLayer.setVisible(false);
      this.aerialMapLayer.setVisible(false);
      //get all layers
      const layersArray = this.map.getLayers().getArray();
      // go through all layer and delete previous layer added
      layersArray.forEach((element) => {
        if (element.type === "TILE") {
          if (element.getProperties().title === "newLayer") {
            this.map.removeLayer(element);
          }
        }
      });
      if (type !== "newLayer") {
        if (type === "openStreetMap") {
          this.roadMapLayer.setSource(this.openStreetMapSource);
          this.roadMapLayer.setVisible(true);
        } else {
          if (type === "roadMapLayer") {
            this[type].setSource(this.roadMapSource);
          }
          this[type].setVisible(true);
        }
      } else {
        //create new layer
        const layer = new TileLayer({
          title: "newLayer",
          visible: true,
          source: new XYZ({
            url: selectedLayer.url,
          }),
        });
        //add layer to map
        this.map.getLayers().insertAt(0, layer);
      }

      this.map.updateSize();
    },

    /** Returns the actual scale of the map **/
    getScale() {
      let resolution = this.mapView.getResolution();
      let projection = this.mapView.getProjection();
      let dpi = 25.4 / 0.28;
      let units = projection.getUnits();
      let mpu = METERS_PER_UNIT[units];
      return resolution * mpu * 39.37 * dpi;
    },

    changeScale(value) {
      let projection = this.mapView.getProjection();
      let dpi = 25.4 / 0.28;
      let units = projection.getUnits();
      let mpu = METERS_PER_UNIT[units];
      let resolution = value / dpi / 39.37 / mpu;
      this.mapView.setResolution(resolution);
    },

    /** Initializes google street view by creating a street view, it's respective panorama and a
     * responsive marker for the openLayers map **/
    initializeStreetView() {
      let centerCoordinates = this.getCenterCoordinates();
      this.googleMap = this.createGoogleMap(centerCoordinates);
      this.panoramaMap = this.createPanoramaMap(centerCoordinates);
      this.streetViewService = new google.maps.StreetViewService();

      this.googleMap.setMapTypeId("hybrid");
      this.googleMap.setTilt(0);
      this.googleMap.setStreetView(this.panoramaMap);

      this.createStreetViewPointerLayer();

      this.panoramaMap.addListener("position_changed", () => {
        this.onPanoramaPositionChange();
      });
      this.panoramaMap.addListener("pov_changed", () => {
        this.updateStreetViewPointerRotation();
      });
      this.googleMap.addListener("zoom_changed", () => {
        this.onGoogleMapZoomChange();
      });
    },

    createGoogleMap(centerLocation) {
      return new google.maps.Map(document.getElementById("google-map"), {
        zoom: 20,
        center: centerLocation,
        mapTypeId: "satellite",
        rotateControl: false,
        controlSize: 32,
      });
    },

    createPanoramaMap(centerLocation) {
      return new google.maps.StreetViewPanorama(
        document.getElementById("street-view"),
        {
          position: centerLocation,
          pov: {
            heading: 165,
            pitch: 0,
          },
          zoom: 1,
          controlSize: 32,
        }
      );
    },

    createStreetViewPointerLayer() {
      this.createStreetViewPointer();

      let vectorSourceStreetView = new VectorSource({
        features: [this.streetViewPointer],
      });
      this.streetViewPointerLayer = new VectorLayer({
        source: vectorSourceStreetView,
      });

      this.map.addLayer(this.streetViewPointerLayer);
      this.streetViewPointerLayer.setVisible(false);
    },

    createStreetViewPointer() {
      let streetViewPointer = new Feature({
        geometry: new Point([0, 0]),
      });
      streetViewPointer.setStyle(
        new Style({
          image: new Icon({
            anchor: [15, 15],
            rotation: 0,
            anchorXUnits: "pixels",
            anchorYUnits: "pixels",
            opacity: 1,
            src: require("@/assets/my_position.png"),
          }),
        })
      );
      this.streetViewPointer = streetViewPointer;
    },

    /**
     * Changes the openLayers map, the google street view map and the pointer position
     * based on a position change on the panorama
     */
    onPanoramaPositionChange() {
      if (this.showStreetView) {
        let pos = this.panoramaMap.getPosition();
        this.googleMap.setCenter(pos);
        this.centerMap(pos.lng(), pos.lat());
        this.mapView.setZoom(this.googleMap.getZoom());

        this.streetViewPointer
          .getGeometry()
          .setCoordinates(
            transform([pos.lng(), pos.lat()], "EPSG:4326", "EPSG:3857")
          );
        this.updateStreetViewPointerRotation();
      }
    },

    updateStreetViewPointerRotation() {
      if (this.showStreetView) {
        let pointerStyle = this.streetViewPointer.getStyle();
        let icon = pointerStyle.getImage();
        let rotation =
          ((this.panoramaMap.getPov().heading - 90) * Math.PI) / 180;
        icon.setRotation(rotation);

        this.streetViewPointerLayer.getSource().dispatchEvent("change");
      }
    },

    /**
     * Changes the openLayers map zoom level based on the street view map zoom level
     */
    onGoogleMapZoomChange() {
      if (this.showStreetView) {
        let pos = this.googleMap.getCenter();
        this.centerMap(pos.lng(), pos.lat());
        this.mapView.setZoom(this.googleMap.getZoom());
      }
    },

    /** Enables/Disables google's street view with the actual map and it's panorama. The map position is the same as
     * the actual openLayers map center position **/
    toggleStreetView() {
      if (this.showStreetView) {
        this.deactivateStreetView();
      } else {
        this.activateStreetView();
      }
    },

    deactivateStreetView() {
      this.showStreetView = false;
      this.streetViewPointerLayer.setVisible(false);

      this.$nextTick(() => this.map.updateSize());
    },

    activateStreetView() {
      this.showStreetView = true;

      if (!this.streetViewPointerLayer) {
        this.createStreetViewPointerLayer();
      }
      this.streetViewPointerLayer.setVisible(true);

      let centerCoordinates = this.getCenterCoordinates();
      this.panoramaMap.setPosition(centerCoordinates);

      let pos = this.panoramaMap.getPosition();
      this.streetViewPointer
        .getGeometry()
        .setCoordinates(
          transform([pos.lng(), pos.lat()], "EPSG:4326", "EPSG:3857")
        );

      this.$nextTick(() => {
        let centerCoordinates = this.getCenterCoordinates();
        this.googleMap.setCenter(centerCoordinates);

        this.streetViewService.getPanorama(
          { location: centerCoordinates, radius: 50 },
          (data, status) => {
            if (status !== "OK") {
              this.panoramaMap.setVisible(false);
            }
          }
        );
        this.map.updateSize();
        this.resizeStreetView();
      });
    },

    /** Resizes the google street view panorama and map **/
    resizeStreetView() {
      setTimeout(() => {
        google.maps.event.trigger(this.googleMap, "resize");
        google.maps.event.trigger(this.panoramaMap, "resize");
      }, 500);
    },

    getCenterCoordinates() {
      let coords = transform(
        this.mapView.getCenter(),
        "EPSG:3857",
        "EPSG:4326"
      );
      let lng = coords[0];
      let lat = coords[1];
      lng = lng % 360;
      if (lng > 180) {
        lng = lng - 360;
      } else if (lng < -180) {
        lng = 360 + lng;
      }

      return { lat: lat, lng: lng };
    },

    /** Centers the openLayers map on the coordinates received as parameter **/
    centerMap(lng, lat) {
      this.mapView.setCenter(
        transform([parseFloat(lng), parseFloat(lat)], "EPSG:4326", "EPSG:3857")
      );
      this.updateURL(
        this.$route.params.workspace_uuid,
        lat,
        lng,
        this.mapView.getZoom()
      );
    },

    /** Centers the openLayers map on the coordinates received as parameter **/
    centerMapFromCoordinatesForm(coordinates) {
      let projection = "EPSG:" + this.currentProjection;
      let lat = parseFloat(coordinates[0]);
      let long = parseFloat(coordinates[1]);

      let position = [long, lat];

      if (this.currentProjection != null && this.currentProjection != 4326) {
        position = transform([lat, long], projection, "EPSG:4326");
      }

      let pointJson = {
        type: "Point",
        coordinates: position,
      };
      position = JSON.stringify(pointJson);
      this.$nextTick(() => {
        this.SET_OBJECT_TO_ZOOM_ON_MAP(undefined).then(() => {
          this.SET_OBJECT_TO_ZOOM_ON_MAP(position);
        });
      });
      this.updateURL(
        this.$route.params.workspace_uuid,
        long,
        lat,
        this.mapView.getZoom()
      );
    },

    setInitialSavedPosition() {
      let workspaceUUID;
      let position;
      let newMapData = JSON.parse(sessionStorage.getItem("new_map_data"));
      if (newMapData) {
        workspaceUUID = newMapData.workspace;
        position = newMapData.position;
      }

      if (this.$store.getters.workspace.uuid === workspaceUUID && position) {
        this.setPos(position);
      } else {
        this.centerOnWorkspace();
      }
    },

    /** Sets the map in the position received as parameter **/
    setPos(position) {
      if (position !== "undefined") {
        position = position.split(";");
        position = position.map((item) => {
          return parseFloat(item);
        });
        this.mapView.fit(position, this.map.getSize());
        this.mapView.setZoom(this.mapView.getZoom() + 1);
      }
    },

    centerOnWorkspace() {
      let workspaceUUID = this.$store.getters.workspace.uuid;
      if (workspaceUUID) {
        this.loading = true;
        this.fetchWorkspaceByUUID({
          uuid: workspaceUUID,
          includes: null,
          fields: "wp_bound_box,wp_id",
        })
          .then((workspace) => {
            let bbox = workspace.wp_bound_box;
            let string = bbox.split(" ");
            let minX = string[0].replace("BOX(", "");
            let minY = string[1].split(",")[0];
            let maxX = string[1].split(",")[1];
            let maxY = string[2].replace(")", "");

            let ext = boundingExtent([
              [+minX, +minY],
              [+maxX, +maxY],
            ]);
            let a = transformExtent(ext, "EPSG:4326", "EPSG:3857");

            this.mapView.fit(a, this.map.getSize());
            this.updateGeoserverLayers();
            let lat = ((parseFloat(minX) + parseFloat(maxX)) / 2.0).toFixed(6);
            let long = ((parseFloat(minY) + parseFloat(maxY)) / 2.0).toFixed(6);
            this.updateURL(workspaceUUID, lat, long, this.mapView.getZoom());
          })
          .catch((error) => console.error(error))
          .then(() => {
            this.loading = false;
          });
      }
    },

    updateGeoserverLayers() {
      this.objectsImageSource.updateParams({
        LAYERS: this.getActiveLayers(),
        viewparams: this.getViewParameters(),
        time: Date.now(),
      });
    },

    setSavedMapType() {
      let savedMapType = localStorage.getItem("map_type");

      if (savedMapType) {
        this.changeLocalMapStyle(savedMapType);
      } else {
        this.isDarkMap = false;
      }
    },

    /**
     * Registers all the projections to be used besides the ones pre-defined.
     */
    registerProjections() {
      proj4.defs(
        "EPSG:2154",
        "+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
      );
      proj4.defs(
        "EPSG:27700",
        "+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs"
      );
      proj4.defs(
        "EPSG:3763",
        "+proj=tmerc +lat_0=39.66825833333333 +lon_0=-8.133108333333334 +k=1 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
      );
      proj4.defs(
        "EPSG:27493",
        "+proj=tmerc +lat_0=39.66666666666666 +lon_0=-8.131906111111112 +k=1 +x_0=180.598 +y_0=-86.98999999999999 +ellps=intl +towgs84=-223.237,110.193,36.649,0,0,0,0 +units=m +no_defs"
      );
      register(proj4);
    },

    createModifyInteraction() {
      this.modifyInteraction = new Modify({ source: this.drawLayerSource });
      this.map.addInteraction(this.modifyInteraction);
      this.modifyInteraction.setActive(false);
    },

    //////////////////////////////
    /*Heatmap*/
    //////////////////////////////

    /**
     * Create layer to heatMap
     * @returns {Heatmap}
     */
    createHeatMap() {
      this.heatmapLayerSource = new VectorSource();
      return new HeatmapLayer({
        title: "HeatMap",
        source: this.heatmapLayerSource,
        blur: 20,
        radius: 10,
        weight: function (feature) {
          return 10;
        },
      });
    },

    /**
     * Add features in heatmapLayerSource
     */
    generateHeatmap(allGeom) {
      this.heatmapLayerSource.clear();
      for (const geom of allGeom) {
        let features = new GeoJSON().readFeatures(geom, {
          featureProjection: "EPSG:3857",
        });

        let feature = features[0];
        this.heatmapLayerSource.addFeature(feature);
      }
    },

    //////////////////////////////
    /*Object interaction methods*/
    //////////////////////////////

    getObjects(e, objectType = null) {
      return new Promise((resolve) => {
        let url = this.getObjectsImageSourceURL(e.coordinate);
        this.loading = true;
        this.fetchFeaturesFromGeoServer({ data: url })
          .then((data) => {
            let features;
            if (objectType === 'fc_fdp_zone') {
              features = data.features.filter(item => item.id.includes('fc_fiber_distribution_point'));
              if (features.length === 0) {
                this.$toasted.global.toast_error('Object Not Found!')
              }
            } else if (objectType === 'fc_olt_zone') {
              features = data.features.filter(item => item.id.includes('fc_optical_line_termination'));
              if (features.length === 0) {
                this.$toasted.global.toast_error('Object Not Found!')
              }
            } else if (objectType === 'fc_closure_zone') {
              features = data.features.filter(item => item.properties.object_type === 'closure');
              if (features.length === 0) {
                this.$toasted.global.toast_error('Object Not Found!')
              }
            } else {
              features = data.features;
              if (features.length === 0) {
                this.$toasted.global.toast_error('Object Not Found!')
              }
            }
            let numberOfFeatures = data.numberReturned;

            let isCreate = this.currentActionType === "create";
            let isPathInspector = this.currentActionType === "pathInspector";

            this.contextMenuPixel = e.pixel;
            this.contextMenuCoords = e.coordinate;

            let intersectedAreas = this.intersectedMeasurementAreas();
            numberOfFeatures += intersectedAreas.length;

            if (
              ((isCreate || isPathInspector) && numberOfFeatures > 0) ||
              numberOfFeatures > 1
            ) {
              this.prepareObjectList(features, objectType);
            } else if (numberOfFeatures === 1) {
              this.selectSingleObject(features);
            }
          })
          .catch((error) => console.error(error))
          .then(() => {
            this.loading = false;
            resolve();
          });
      });
    },

    getObjectsImageSourceURL(coordinates) {
      let layers = this.objectsImageSource.params_.LAYERS;
      //Excludes the signal layer for the object list
      layers = layers.replace(
        process.env.GEOSERVER_WORKSPACE + ":fc_signal,",
        ""
      );
      let resolution = this.mapView.getResolution();
      let projection = this.mapView.getProjection();

      return this.objectsImageSource.getGetFeatureInfoUrl(
        coordinates,
        resolution,
        projection,
        {
          QUERY_LAYERS: layers,
          LAYERS: layers,
          INFO_FORMAT: "application/json",
          FEATURE_COUNT: "100",
          scale: this.getScale(),
          force_workspace_filter: this.forceWorkspaceFilter,
        }
      );
    },

    selectSingleObject(features) {
      let feature = features[0];
      let objectIdentifiers = this.getObjectIdentifiers(feature);
      if (objectIdentifiers) {
        this.getObjectDataFromList(objectIdentifiers);
      }
    },

    prepareObjectList(features, type) {
      switch (type) {
        case "fc_building":
        case "fc_manhole":
        case "fc_pole":
        case "fc_cabinet":
        case "fc_toby_box":
        case "fc_virtual":
        case "fc_fiber_distribution_point":
        case "fc_optical_line_termination":
        case "fc_site":
          if (this.userHasFibercloudPermission("pathway_edit_geom")) {
            this.objectList.list = this.filterObjectList(features, [
              { key: "objectType", value: "pathway" },
            ]);
            this.includeNoneOptionInObjectList();
          } else {
            this.objectList.list = [];
          }
          break;

        case "fc_pathway":
          this.objectList.list = this.filterObjectList(features, [
            { key: "objectType", value: "technical_point" },
            { key: "objectType", value: "technical_site" },
            { key: "objectType", value: "building" },
            { key: "objectType", value: "pathway" },
          ]);
          this.includeNoneOptionInObjectList();
          break;

        case "fc_closure":
          this.objectList.list = this.filterObjectList(features, [
            { key: "objectType", value: "technical_point" },
          ]);
          break;

        case "fc_technical_local":
          this.getObjectListForTechnicalLocal(features);
          break;

        case "fc_fiber_terminal_box":
          this.getObjectListForCluster(features);
          break;

        case "fc_cable":
          this.getObjectListForCable(features);
          break;

        case "fc_field_cable":
          this.getObjectListForFieldCable(features);
          break;

        case "fc_slack":
          this.getObjectListForSlack(features);
          break;

        case "fc_blockage":
          this.getObjectListForBlockage(features);
          break;

        case "fc_annotation_point":
          this.objectList.list = [];
          break;

        case "fc_microduct_bundle":
          this.getObjectListForBundle(features);
          break;

        case "pathInspector":
          this.getObjectListForPathInspector(features);
          break;

        default:
          this.objectList.list = [];
          features.forEach((feature) => {
            let featureIdentifiers = this.getObjectIdentifiers(feature);

            if (featureIdentifiers) {
              this.objectList.list.push(featureIdentifiers);
            }
          });
          break;
      }

      let intersectedAreas = this.intersectedMeasurementAreas();
      if (intersectedAreas.length > 0) {
        this.prepareMeasurementAreasList(intersectedAreas);
        this.objectList.open = true;
      }

      if (!this.isObjectListEmpty()) {
        this.joinCableMenu.open = false;
        this.objectList.open = true;
      }
    },

    includeNoneOptionInObjectList() {
      if (!this.isObjectListEmpty()) {
        this.objectList.list.unshift({ none: true });
      }
    },

    getObjectListForTechnicalLocal(features) {
      let conditions = [];
      let activeLayers = this.getActiveLayersNamesList(
        JSON.parse(sessionStorage.getItem("active_layers"))
      );

      if (activeLayers.includes("fc_technical_local_fdp")) {
        conditions.push({ key: "type", value: "Primary Node" });
      }

      if (activeLayers.includes("fc_technical_local_olt")) {
        conditions.push({ key: "type", value: "Central Office" });
      }

      if (activeLayers.includes("fc_building")) {
        conditions.push({ key: "objectType", value: "building" });
      }

      if (activeLayers.includes("fc_site")) {
        conditions.push({ key: "objectType", value: "site" });
      }

      this.objectList.list = this.filterObjectList(features, conditions);
      if (this.isObjectListEmpty()) {
        this.$toasted.global.toast_error(this.$t("TechnicalLocalCreateError"));
      }
      if (this.objectList.list.length === 1) {
        this.getObjectDataFromList(this.objectList.list[0]);
      }
    },

    getObjectListForCluster(features) {
      let conditions = [];
      if (this.clusterWKT.length === 0) {
        conditions = [{ key: "objectType", value: "closure" }];
      } else if (this.clusterWKT.length > 0) {
        conditions = [{ key: "objectType", value: "building" }];
      }
      this.objectList.list = this.filterObjectList(features, conditions);
      if (this.objectList.list.length === 1) {
        this.getObjectDataFromList(this.objectList.list[0]);
      }
      if (this.isObjectListEmpty()) {
        bus.$emit("highlight-cluster-box");
      }
    },

    getObjectListForWorkOrder(features) {
      features.forEach((item) => {
        if (item.id.startsWith("field")) {
          this.objectList.list = features;
        }
      });
    },

    getObjectListForCable(features) {
      let conditions = [];
      let tier = parseInt(this.cable.tier);
      if (this.lineStringWKT.length === 0) {
        conditions.push({ key: "objectType", value: "closure" });
        if (tier === 1) {
          conditions.push({ key: "type", value: "Technical Local MDU" })
        } else if (tier === 2) {
          conditions.push({ key: "type", value: "Technical Local FDP" });
          conditions.push({ key: "type", value: "Technical Local Site" });
        } else if (tier === 3) {
          conditions.push({ key: "type", value: "Technical Local FDP" });
          conditions.push({ key: "type", value: "Technical Local OLT" });
          conditions.push({ key: "type", value: "Technical Local Site" });
        } else if (tier === 4) {
          conditions.push({ key: "type", value: "Technical Local OLT" });
        }
      } else {
        switch (tier) {
          case 1:
            if (
              this.lineStringWKT[this.lineStringWKT.length - 1].type &&
              this.lineStringWKT[this.lineStringWKT.length - 1].type !==
                "building"
            ) {
              if (this.lineStringWKT[this.lineStringWKT.length - 1].layerName === "fc_technical_local_mdu") {
                conditions.push({ key: "buildingType", value: "SDU"})
                conditions.push({ key: "type", value: "Technical Local MDU" });
              } else {
                conditions.push({ key: "objectType", value: "technical_point" });
                conditions.push({ key: "objectType", value: "building" });
              }
            }
            break;
          case 2:
            if (
              this.lineStringWKT[this.lineStringWKT.length - 1].type &&
              this.lineStringWKT[this.lineStringWKT.length - 1].layerName !==
                "fc_technical_local_mdu"
            ) {
              conditions.push({ key: "objectType", value: "technical_point" });
              conditions.push({ key: "objectType", value: "closure" });
              conditions.push({ key: "type", value: "Technical Local MDU" });
            }
            break;
          case 3:
            conditions.push({ key: "objectType", value: "technical_point" });
            conditions.push({ key: "objectType", value: "closure" });
            conditions.push({ key: "objectType", value: "technical_local" });
            break;
          case 4:
            conditions.push({ key: "objectType", value: "technical_point" });
            conditions.push({ key: "objectType", value: "closure" });
            conditions.push({ key: "type", value: "Technical Local OLT" });
        }
      }
      this.objectList.list = this.filterObjectList(features, conditions);
      if (this.objectList.list.length === 1) {
        this.getObjectDataFromList(this.objectList.list[0]);
      } else if (this.isObjectListEmpty()) {
        bus.$emit("highlight-cable-box");
      }
    },

    getObjectListForBundle(features) {
      let conditions = [];
      conditions.push({ key: "objectType", value: "technical_point" });
      conditions.push({ key: "objectType", value: "building" });
      conditions.push({ key: "objectType", value: "technical_local" });

      this.objectList.list = this.filterObjectList(features, conditions);

      if (this.isObjectListEmpty()) {
        bus.$emit("highlight-bundle-box");
      }

      if (this.objectList.list.length === 1) {
        this.getObjectDataFromList(this.objectList.list[0]);
      }
    },

    getObjectListForFieldCable(features) {
      this.objectList.list = this.filterObjectList(features, [
        { key: "type", value: "Pole" },
      ]);

      if (this.isObjectListEmpty()) {
        if (fieldCableNodes.length > 0) {
          this.fieldCableNodes.push({
            type: "vertice_point",
            uuid: "None",
            id: null,
            label: null,
          });
        }
      }
      if (this.objectList.list.length === 1) {
        this.getObjectDataFromList(this.objectList.list[0]);
      }
    },

    getObjectListForSlack(features) {
      this.objectList.list = this.filterObjectList(features, [
        { key: "type", value: "Cable" },
      ]);

      if (this.isObjectListEmpty()) {
        this.$toasted.global.toast_error(this.$i18n.t("NoCableDetected"));
      }
    },

    getObjectListForBlockage(features){
      this.objectList.list = this.filterObjectList(features, [
        { key: "type", value: "Pathway" },
        { key: "type", value: "Pole" },
        { key: "type", value: "Chamber" },
      ]);
      this.includeNoneOptionInObjectList();
    },

    filterObjectList(features, conditions) {
      let filteredObjectList = [];
      for (const feature of features) {
        let featureIdentifiers = this.getObjectIdentifiers(feature);
        if (
          featureIdentifiers &&
          !featureIdentifiers.layerName.includes("base")
        ) {
          for (const condition of conditions) {
            if (featureIdentifiers[condition.key] === condition.value) {
              filteredObjectList.push(featureIdentifiers);
            }
          }
        }
      }
      return filteredObjectList;
    },

    getObjectIdentifiers(feature) {
      let layer = this.getObjectLayer(feature.id);

      if (layer === "fc_phase" || layer === "fc_field_influence_line") {
        return false;
      }

      let initials = this.$store.getters.getLayerInitials(layer);
      let endpoint = this.$store.getters.getLayerEndpoint(layer);
      let typeName = this.$store.getters.getLayerTypeName(layer);

      const objectIdentifiers = {
        type: typeName,
        id: this.getObjectIDNumber(feature.id),
        uuid: feature.properties[initials + "_uuid"],
        endpoint: endpoint,
        layerName: layer,
        coordinates: feature.geometry.coordinates,
        objectType: feature.properties.object_type,
        marked: false,
        geojson: feature.properties.geo_json,
        label: feature.properties[initials + "_label"],
      };

      if (layer === "fc_building" && feature.properties.bd_homes < this.minNumberHPsPerMDU) {
        objectIdentifiers.buildingType = "SDU";
      }

      return objectIdentifiers;
    },

    getObjectLayer(entireID) {
      return entireID.split(".")[0];
    },

    getObjectIDNumber(entireID) {
      return entireID.substr(entireID.indexOf(".") + 1);
    },

    getObjectDataFromList(objectIdentifiers) {
      this.closeObjectList();
      if (!objectIdentifiers.isMeasurementArea) {
        this.UPDATE_SELECTED_OBJECT_IDENTIFIERS(objectIdentifiers);
      }

      let layerName = objectIdentifiers.layerName;
      if (this.currentActionType === "pathInspector") {
        bus.$emit("object-path-inspector", objectIdentifiers);
      } else if (
        this.clickType === "leftClick" &&
        this.currentActionType !== "create" &&
        this.currentActionType !== "move"
      ) {
        this.fetchObjectData().then((objectData) => {
          this.UPDATE_SELECTED_OBJECT_DATA(objectData);
          this.openForm(layerName, "view");
        });
      } else if (
        this.clickType === "rightClick" &&
        this.currentActionType !== "move"
      ) {
        if (objectIdentifiers.isMeasurementArea) {
          this.removeMeasurementAreaHighlightFromObjectOnLeave(
            objectIdentifiers.geojson
          );
          this.SET_TOOL_OBJECT_LAYER(objectIdentifiers.layerName);
          this.$nextTick(() => {
            this.measurementAreaToDelete = objectIdentifiers.geojson;
            this.createArea(
              {
                text_geom: new WKT().writeFeature(objectIdentifiers.geojson),
                projection: 3857,
              },
              objectIdentifiers.isMeasurementArea
            );
          });
        } else {
          this.$nextTick(() => {
            //////
            /// TO REMOVE ONCE BE SUPPORTS
            //////
            if (layerName !== 'fc_microduct_bundle') {
              this.highlightObjectOnHover(objectIdentifiers.geojson);
              this.objectMenu.open = true;
            } else {
              this.$toasted.global.toast_warning("Not Supported Yet");
            }
          });
        }
      } else if (
        this.currentActionType === "create" ||
        this.currentActionType === "move"
      ) {
        if(this.currentActionType){

          this.continueCreatingOrMovingProcess();
        }
      }
    },

    /**
     * Returns the measurement areas that intersect with the clicked coordinates
     */
    intersectedMeasurementAreas() {
      let measurements = this.measurementsLayerSource.featuresRtree_.items_;
      let intersectedAreas = [];
      for (const item in measurements) {
        let geom = measurements[item].value.getGeometry();
        if (
          geom instanceof Polygon &&
          geom.intersectsCoordinate(this.contextMenuCoords)
        ) {
          intersectedAreas.push(measurements[item]);
        }
      }
      return intersectedAreas;
    },

    /**
     * Adds the areas to the object list
     * @param areas - The areas to add to the list
     */
    prepareMeasurementAreasList(areas) {
      for (const area of areas) {
        let areaIdentifiers = this.getAreaIdentifiers(area);
        this.objectList.list.push(areaIdentifiers);
      }
    },

    /**
     * Identifiers for the object menu
     * @param area - The area to identify
     */
    getAreaIdentifiers(area) {
      return {
        type: "Measurement Area",
        id: area.value.ol_uid,
        endpoint: "annotationszones",
        layerName: "fc_annotation_zone",
        coordinates: [area.value.getGeometry().flatCoordinates],
        objectType: "annotation_zone",
        marked: false,
        geojson: area.value,
        label: area.value.ol_uid,
        isMeasurementArea: true,
      };
    },

    closeObjectList() {
      this.objectList.open = false;
      this.objectList.list = [];
    },
    closeForm() {
      this.workorders.actionType = null;
      this.workorders.workorder_uuid = null;
      this.workorders.workOrderOpen = false;
      this.closeObjectMenu();
    },
    openWorkorderForm(workOrderUUID, actionType) {
      this.workorders.workorder_uuid = workOrderUUID;
      this.workorders.actionType = actionType;
      this.workorders.workOrderOpen = true;
      this.closeObjectMenu();
      this.closeObjectList();
    },

    openForm(layer, actionType, isMeasurementArea = null) {
      this.$refs.multiObjectForm.openNewForm(
        layer,
        actionType,
        this.wkt,
        this.showStreetView,
        this.linkObjectToPathway,
        this.linkPathwayToPathway,
        this.pathwayNodes,
        this.technicalPointData,
        this.technicalSiteData,
        this.cable,
        this.lineStringWKT,
        this.fieldCableNodes,
        this.newSlack,
        this.newBlockage,
        isMeasurementArea
      );

      this.closeObjectList();
    },

    highlightObjectOnHover(geoJson) {
      let feature;
      if (geoJson instanceof Feature) {
        feature = geoJson;
      } else {
        let features = new GeoJSON().readFeatures(geoJson, {
          featureProjection: "EPSG:3857",
        });
        feature = features[0];
      }
      let geometryType = feature.getGeometry().getType();
      let highlightStyle = {
        point: {
          radius: 15,
          strokeWidth: 2,
          fillColor: [255, 255, 255],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(250, 250, 250, 0.3)",
        },
        line: {
          strokeWidth: 10,
          strokeColor: [255, 255, 255],
          strokeWidthSecond: 8,
        },
      };

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.highlightLayerSource.addFeature(feature);
    },

    getHighlightFeatureStyle(type, highlightStyle) {
      switch (type) {
        case "Point":
          return new Style({
            image: new CircleStyle({
              radius: highlightStyle.point.radius,
              stroke: new Stroke({
                color: [0, 0, 0],
                width: highlightStyle.point.strokeWidth,
                zIndex: 1,
              }),
              fill: new Fill({
                color: highlightStyle.point.fillColor,
                zIndex: 1,
              }),
            }),
          });
        case "Polygon":
        case "MultiPolygon":
          return new Style({
            stroke: new Stroke({
              color: [0, 0, 0],
              width: highlightStyle.polygon.strokeWidth,
              zIndex: 1,
            }),
            fill: new Fill({
              color: highlightStyle.polygon.fillColor,
              zIndex: 1,
            }),
          });
        case "LineString":
        case "MultiLineString":
          return [
            new Style({
              stroke: new Stroke({
                color: [0, 0, 0],
                width: highlightStyle.line.strokeWidth,
                zIndex: 1,
              }),
            }),
            new Style({
              stroke: new Stroke({
                color: highlightStyle.line.strokeColor,
                width: highlightStyle.line.strokeWidthSecond,
                zIndex: 1,
              }),
            }),
          ];
      }
    },

    removeHighlightFromObjectOnLeave() {
      this.highlightLayerSource.clear();
    },

    /**
     * Removes the highlighted area and restores the normal styling of the area
     * @param object - The highlighted measurement area
     */
    removeMeasurementAreaHighlightFromObjectOnLeave(object) {
      this.highlightLayerSource.clear();
      object.setStyle(
        new Style({
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
          }),
          stroke: new Stroke({
            color: "#ffcc33",
            width: 2,
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: "#ffcc33",
            }),
          }),
        })
      );
    },

    continueCreatingOrMovingProcess() {
      let layerOfObjectBeingCreatedOrMoved;
      if (
        this.closureWithoutTechPoint.passingObjects &&
        this.closureWithoutTechPoint.technicalPoint
      ) {
        layerOfObjectBeingCreatedOrMoved =
          this.closureWithoutTechPoint.technicalPoint;
      } else {
        layerOfObjectBeingCreatedOrMoved = this.selectedToolObjectLayer;
      }
      let none = this.selectedObjectIdentifiers.none;
      let uuid = this.selectedObjectIdentifiers.uuid;
      let id = this.selectedObjectIdentifiers.id;
      let objectType = this.selectedObjectIdentifiers.type;
      let objectTypeRoute = this.selectedObjectIdentifiers.objectType;
      switch (layerOfObjectBeingCreatedOrMoved) {
        case "fc_building":
        case "fc_manhole":
        case "fc_pole":
        case "fc_cabinet":
        case "fc_toby_box":
        case "fc_virtual":
        case "fc_fiber_distribution_point":
        case "fc_optical_line_termination":
        case "fc_site":
          if (none) {
            this.respondToObjectSplitPathway(false);
          } else if (
            objectTypeRoute.includes("pathway") &&
            this.userHasFibercloudPermission("pathway_edit_geom")
          ) {
            if (
              layerOfObjectBeingCreatedOrMoved === "fc_manhole" ||
              layerOfObjectBeingCreatedOrMoved === "fc_pole" ||
              layerOfObjectBeingCreatedOrMoved === "fc_cabinet" ||
              layerOfObjectBeingCreatedOrMoved === "fc_toby_box" ||
              layerOfObjectBeingCreatedOrMoved === "fc_virtual"
            ) {
              this.linkTechPointToPathwayDialog = true;
            } else {
              this.respondToObjectSplitPathway(true);
            }
          }
          break;

        case "fc_fdp_zone":
          if(this.selectedObjectIdentifiers.type === "Primary Node"){
            window.localStorage.setItem('zone_id', this.selectedObjectIdentifiers.id);
            this.startArea(this.mapEvent);
            this.escKeyAction({key: "Escape"});
          }else{
            this.$toasted.global.toast_error("You have to choose a primary node");
          }
          break;

        case "fc_olt_zone":
          if(this.selectedObjectIdentifiers.type === "Central Office"){
            window.localStorage.setItem('zone_id', this.selectedObjectIdentifiers.id);
            this.startArea(this.mapEvent);
            this.escKeyAction({key: "Escape"});
          }else{
            this.$toasted.global.toast_error("You have to choose a central office");
          }
          break;

        case "fc_closure_zone":
          if(this.selectedObjectIdentifiers.type === "Closure"){
            window.localStorage.setItem('zone_id', this.selectedObjectIdentifiers.id);
            this.startArea(this.mapEvent);
            this.escKeyAction({key: "Escape"});
          }else{
            this.$toasted.global.toast_error("You have to choose a closure");
          }
          break;

        case "fc_pathway":
          if (none) {
            this.linkPathwayToPathway.onFirstPoint = false;
          } else {
            this.connectPathway();
          }
          break;

        case "fc_closure":
          if (this.currentActionType === "create") {
            this.startClosureCreation();
          } else if (this.currentActionType === "move") {
            this.moveClosure();
          }
          break;

        case "fc_technical_local":
          this.fetchObjectData().then((objectData) => {
            this.startTechnicalLocalCreation(objectType, objectData);
          });
          break;

        case "fc_cable":
          this.addPointToCable(objectTypeRoute);
          break;

        case "fc_microduct_bundle":
          this.addPointToBundle(objectTypeRoute);
          break;

        case "fc_field_cable":
          if (objectTypeRoute.includes("technical_point")) {
            this.fieldCableNodes.push({
              type: objectTypeRoute,
              uuid: uuid,
              id: this.selectedObjectIdentifiers.id,
              label: this.selectedObjectIdentifiers.label,
            });
          }
          break;

        case "fc_slack":
          this.connectSlack();
          break;

        case "fc_blockage":
          this.connectBlockage();
          break;

        case "fc_fiber_terminal_box":
          this.addPointToCluster(objectTypeRoute, uuid);
          break;

        case "fc_cluster_tool":
          this.addPointToCluster(objectTypeRoute, id);
          break;
      }
    },

    snapTechPointToPathway() {
      this.linkTechPointToPathwayDialog = false;

      //get nearest node of the pathway
      let coords = this.getTechPointSnapCoordinates();
      let pointCoords = [];
      this.getNodeWKT(coords, (pointWKT) => {
        pointCoords = this.getPointCoordsFromWKT(pointWKT);
      });

      this.linkObjectToPathway.pathway = this.selectedObjectIdentifiers;
      this.linkObjectToPathway.pathway.uuid =
        this.selectedObjectIdentifiers.uuid;

      //convert projection
      let lat = parseFloat(pointCoords[0]);
      let long = parseFloat(pointCoords[1]);
      let position = transform([lat, long], "EPSG:4326", "EPSG:3857");

      this.getNodeWKT(position, (pointWKT) => {
        this.wkt = pointWKT;
      });

      if (this.currentActionType === "create") {
        if (this.closureWithoutTechPoint.technicalPoint) {
          this.openForm(this.closureWithoutTechPoint.technicalPoint, "create");
        } else {
          this.openForm(this.selectedToolObjectLayer, "create");
        }
      } else if (this.currentActionType === "move") {
        let updatedObjectData = { uuid: this.moveObjectData.uuid };

        updatedObjectData.node = { text_geom: this.wkt, projection: 3857 };

        updatedObjectData.node.pathway = this.linkObjectToPathway.pathway;
        updatedObjectData.node.delete_pathways_association = "false";

        this.moveObjectRequest(updatedObjectData).then(() => {
          this.linkObjectToPathway.pathway.uuid = null;
          this.linkObjectToPathway.split = false;
        });
      }
    },

    getTechPointSnapCoordinates() {
      let selectedObjectCoords = this.selectedObjectIdentifiers.coordinates;
      let interceptionPoint = this.getPointCoordsFromWKT(this.wkt);
      let interceptionPointTip = this.getTransformedGeom(interceptionPoint);

      let closestTechPointTipCoordinate;

      let distanceLastTip;

      for (let coords of selectedObjectCoords) {
        let coordsTip = this.getTransformedGeom(coords);
        let distanceCoordsTip = this.getDistFromPathwayTipToIntersection(
          coords,
          interceptionPointTip
        );
        if (!distanceLastTip) {
          closestTechPointTipCoordinate = coordsTip.flatCoordinates;
          distanceLastTip = distanceCoordsTip;
        } else if (distanceLastTip > distanceCoordsTip) {
          closestTechPointTipCoordinate = coordsTip.flatCoordinates;
        }
      }
      return closestTechPointTipCoordinate;
    },

    cancelConnectTechPointToPathway() {
      this.respondToObjectSplitPathway(false);
    },

    async fetchObjectData() {
      try {
        this.loading = true;
        let resp = await this.fetchObjectByUUID({
          endpoint: this.selectedObjectIdentifiers.endpoint,
          objectUUID: this.selectedObjectIdentifiers.uuid,
          fieldsToInclude: this.getFieldsToInclude(),
          fieldsToAppend: this.getFieldsToAppend(),
        });
        this.loading = false;
        return resp.data.data[0];
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },

    getFieldsToInclude() {
      switch (this.selectedObjectIdentifiers.endpoint) {
        case "closures":
          return "technicalpoint,fiberterminalbox,images";
        case "technicalpoints":
          return "images";
        case "buildings":
          return "images,technicalsite,technicallocals,customer_premises";
        case "sites":
        case "technicallocals":
          return "technicalsite";
        case "technicalsites":
          return "technicallocals,images";
        case "cables":
          return "conducts";
        default:
          return "";
      }
    },

    getFieldsToAppend() {
      let fieldsToAppend = [];

      if (this.selectedObjectIdentifiers.endpoint === "asset-reservations") {
        fieldsToAppend.push("associated_assets");
        fieldsToAppend.push("associated_network_elements");
      }
      return fieldsToAppend.join(",");
    },

    startClosureCreation() {
      this.technicalPointData = null;
      if (this.closureWithoutTechPoint.dialog) {
        this.closureWithoutTechPoint.dialog = false;
        let technicalPoint = null;
        switch (this.$store.getters.getClosureWithoutTechPoint) {
          case "C":
            technicalPoint = "fc_manhole";
            break;
          case "B":
            technicalPoint = "fc_cabinet";
            break;
          case "A":
            technicalPoint = "fc_pole";
            break;
          case "T":
            technicalPoint = "fc_toby_box";
            break;
          case "V":
            technicalPoint = "fc_virtual";
            break;
          default:
            break;
        }
        this.nextForm = "fc_closure";

        this.getObjects(
          this.closureWithoutTechPoint.event,
          technicalPoint
        ).then(() => {
          if (this.isObjectListEmpty()) {
            this.openForm(technicalPoint, "create");
          } else {
            this.closureWithoutTechPoint.passingObjects = true;
            this.closureWithoutTechPoint.technicalPoint = technicalPoint;
          }
        });
      } else {
        this.fetchObjectData().then((objectData) => {
          if (objectData.hasOwnProperty("tp_id")) {
            this.technicalPointData = objectData;
            this.fetchCablesPassingInTechPoint(this.technicalPointData.tp_uuid)
              .then((cables) => {
                if (cables && cables.length > 0) {
                  this.cablesSplitDialog.open = true;
                } else {
                  this.openForm("fc_closure", "create");
                }
              })
              .catch((e) => {
                console.error(e);
              });
          } else {
            this.openForm("fc_closure", "create");
          }
        });
      }
    },

    respondToObjectSplitPathway(split) {
      this.linkTechPointToPathwayDialog = false;
      this.linkObjectToPathway.split = split;

      if (split) {
        this.linkObjectToPathway.pathway.uuid =
          this.selectedObjectIdentifiers.uuid;
      }
      if (this.currentActionType === "create") {
        if (this.closureWithoutTechPoint.technicalPoint) {
          this.openForm(this.closureWithoutTechPoint.technicalPoint, "create");
        } else {
          this.openForm(this.selectedToolObjectLayer, "create");
        }
      } else if (this.currentActionType === "move") {
        let updatedObjectData = this.getUpdatedPointObjectData();
        this.moveObjectRequest(updatedObjectData).then(() => {
          this.linkObjectToPathway.pathway.uuid = null;
          this.linkObjectToPathway.split = false;
        });
      }
    },

    startTechnicalLocalCreation(objectType, techSiteData) {
      if (this.canCreateTechnicalLocal(objectType, techSiteData)) {
        let specificTechnicalLocalType;

        switch (objectType) {
          case "Building":
            this.technicalSiteData = techSiteData.technicalsite
              ? techSiteData.technicalsite
              : techSiteData.technical_site;
            specificTechnicalLocalType = "mdu";
            break;

          case "Site":
            this.technicalSiteData = techSiteData.technicalsite
              ? techSiteData.technicalsite
              : techSiteData.technical_site;
            specificTechnicalLocalType = "site";
            break;

          case "Primary Node":
            this.technicalSiteData = techSiteData;
            specificTechnicalLocalType = "fdp";
            break;

          case "Central Office":
            this.technicalSiteData = techSiteData;
            specificTechnicalLocalType = "olt";
            break;
        }
        this.openForm(
          "fc_technical_local" + "_" + specificTechnicalLocalType,
          "create"
        );
      }
    },

    canCreateTechnicalLocal(objectType, techSiteData) {
      if (this.selectedToolObjectLayer.includes("fc_technical_local")) {
        if (
          objectType === "Primary Node" &&
          techSiteData.hasOwnProperty("technicallocals") &&
          techSiteData.technicallocals.length > 0
        ) {
          this.$toasted.global.toast_error(
            objectType +
              " " +
              techSiteData.ts_id +
              " already contains a technical local"
          );
          return false;
        } else if (objectType === "Building") {
          if (techSiteData.bd_type === "SDU") {
            this.$toasted.global.toast_error(
              "Select a MDU Building or a Technical Site"
            );
            return false;
          } else if (
            techSiteData.hasOwnProperty("technicallocals") &&
            techSiteData.technicallocals !== null
          ) {
            this.$toasted.global.toast_error(
              objectType +
                " " +
                techSiteData.bd_id +
                " already contains a technical local"
            );
            return false;
          }
        }
      }
      return true;
    },

    addPointToCable(objectType) {
      if (
        objectType.includes("building") ||
        objectType.includes("technical_local") ||
        objectType.includes("technical_point") ||
        objectType.includes("technical_site") ||
        objectType.includes("closure")
      ) {
        bus.$emit("add-node-to-cable");
      }
    },

    addPointToBundle(objectType) {
      if (
        objectType.includes("building") ||
        objectType.includes("technical_local") ||
        objectType.includes("technical_point")
      ) {
        bus.$emit("add-node-to-bundle");
      }
    },

    connectSlack() {
      if (
        !this.newSlack.cableUUID ||
        this.selectedObjectIdentifiers.type === "Cable"
      ) {
        let uuid = this.selectedObjectIdentifiers.uuid;
        this.newSlack.cableUUID = uuid;

        let newSlackData = {
          node: {
            text_geom: this.wkt,
            projection: 3857,
          },
          cable: {
            uuid: uuid,
          },
        };

        this.fetchSlackNearestObjects(newSlackData)
          .then((nearestObjects) => {
            this.objectList.list =
              this.getSlackPossibleNodeObjects(nearestObjects);
            if (this.objectList.list.length > 0) {
              this.objectList.list.unshift({ none: true });
              this.objectList.open = true;
            } else if (this.currentActionType === "create") {
              this.openForm("fc_slack", "create");
            }
          })
          .catch((error) => {
            console.error(error);
            if (this.currentActionType === "create") {
              this.openForm("fc_slack", this.currentActionType);
            }
          });
      } else {
        if (!this.selectedObjectIdentifiers.none) {
          this.newSlack.nodeObject = {
            object_uuid: this.selectedObjectIdentifiers.uuid,
            object_type: this.selectedObjectIdentifiers.objectType,
          };
        }
        this.openForm("fc_slack", "create");
      }
    },

    getSlackPossibleNodeObjects(nearestObjects) {
      let possibleNodeObjects = [];
      if (
        typeof nearestObjects === "object" &&
        Object.entries(nearestObjects).length > 0
      ) {
        for (const [objectType, objectsOfType] of Object.entries(
          nearestObjects
        )) {
          if (
            !objectType.includes("pathway") &&
            objectType !== "parent_cable" &&
            objectType !== "child_cable"
          ) {
            for (const nodeObject of objectsOfType) {
              let type;
              let prefix;
              let technicalObject = nodeObject;

              switch (objectType) {
                case "building":
                  type = "Building";
                  prefix = "bd_";
                  technicalObject = nodeObject.technicalsite
                    ? nodeObject.technicalsite
                    : nodeObject.technical_site;
                  break;
                case "site":
                  type = "Site";
                  prefix = "st_";
                  technicalObject = nodeObject.technicalsite
                    ? nodeObject.technicalsite
                    : nodeObject.technical_site;
                  break;
                case "technical_point":
                  type = "TechnicalPoint";
                  prefix = "tp_";
                  break;
                case "technical_site":
                  type = "TechnicalSite";
                  prefix = "ts_";
                  break;
              }

              possibleNodeObjects.push({
                id: nodeObject[prefix + "id"],
                uuid: nodeObject[prefix + "uuid"],
                type: type,
                objectType: objectType,
                geojson: technicalObject.node.nd_geo_json,
                label: nodeObject[prefix + "label"],
                layerName: objectType === "technical_point" ? this.typeChecker(nodeObject[prefix + "physical_type"]) : null
              });
            }
          }
        }
      }

      return possibleNodeObjects;
    },

    respondToConnectSlackToNode(connect, selectedNodeObject) {
      if (connect) {
        let objectUuidKey = Object.keys(selectedNodeObject).find((key) => {
          return key.includes("uuid");
        });
        this.newSlack.nodeObject = {
          uuid: selectedNodeObject[objectUuidKey],
          type: selectedNodeObject.type,
        };
      }

      if (this.currentActionType === "create") {
        this.openForm("fc_slack", "create");
      }
    },

    resetSlackData() {
      this.newSlack.cableUUID = null;
      this.newSlack.nodeObject = null;
      this.newSlack.possibleNodeObjects = [];
    },

    resetBlockageData() {
      this.newBlockage.nodeObject = null;
      this.newBlockage.pathway_id = null;
      this.newBlockage.technical_point_id = null;
    },

    connectBlockage() {
      this.resetBlockageData()

      this.newBlockage.nodeObject = {
        text_geom: this.wkt,
        projection: 3857
      }

      if (!this.selectedObjectIdentifiers.none) {
        this.newBlockage.nodeObject = {
          object_uuid: this.selectedObjectIdentifiers.uuid,
          object_type: this.selectedObjectIdentifiers.layerName,
        };
      }

      if(this.selectedObjectIdentifiers.objectType === "pathway"){
        this.newBlockage.pathway_id = this.selectedObjectIdentifiers.id;
      }
      else{
        this.newBlockage.technical_point_id = this.selectedObjectIdentifiers.id;
      }

      this.openForm("fc_blockage", "create");
    },

    addPointToCluster(objectType, uuid) {
      if (
        this.clusterTool.type === "point" ||
        (this.cluster.type === "point" &&
          (objectType.includes("building") || objectType.includes("closure")))
      ) {
        bus.$emit("add-new-point-to-cluster", {
          uuid: uuid,
          objectTypeRoute: objectType,
        });
      }
    },

    connectPathway() {
      let intersectedObjectType = this.selectedObjectIdentifiers.objectType;
      if (
        intersectedObjectType.includes("pathway") &&
        this.userHasFibercloudPermission("pathway_edit_geom")
      ) {
        this.linkPathwayToPathway.onFirstPoint =
          this.pathwayVerticesCoordinates &&
          this.pathwayVerticesCoordinates.length === 1;
        this.linkPathwayToPathway.dialog = true;
      } else if (
        intersectedObjectType.includes("building") ||
        intersectedObjectType.includes("technical_point") ||
        intersectedObjectType.includes("technical_site") ||
        intersectedObjectType.includes("closure") ||
        intersectedObjectType.includes("site")
      ) {
        this.connectPathwayToObject();
      } else {
        this.checkIfPathwayEndDisconnects();
      }
    },

    connectPathwayToObject() {
      let coords = this.selectedObjectIdentifiers.coordinates;
      let pathwayNode = {
        uuid: this.selectedObjectIdentifiers.uuid,
        type: this.selectedObjectIdentifiers.endpoint,
      };
      let pointCoords = [];
      this.getNodeWKT(coords, (pointWKT) => {
        pointCoords = this.getPointCoordsFromWKT(pointWKT);
      });

      if (this.currentActionType === "create") {
        this.connectNewPathwayToObject(pathwayNode, pointCoords);
      } else if (this.currentActionType === "move") {
        this.connectExistingPathwayToObject(pathwayNode, pointCoords);
      }
    },

    connectNewPathwayToObject(pathwayNode, pointCoords) {
      pathwayNode.vertex_index = this.pathwayVerticesCoordinates.length - 1;
      this.pathwayNodes.push(pathwayNode);
      this.pathwayVerticesCoordinates.pop();
      this.pathwayVerticesCoordinates.push(pointCoords);
      // Finish creating pathways if the last node is a Building (also doesn't allow first node to be a building)
      if (pathwayNode.type === "buildings" && pathwayNode.vertex_index > 0) {
        this.finishPathwayDrawing();
      }
    },

    connectExistingPathwayToObject(pathwayNode, pointCoords) {
      if (this.pathwayBeingMoved.vertexBeingModified === "first") {
        pathwayNode.vertex_index = 0;
        this.pathwayBeingMoved.startDisconnected = false;
      } else if (this.pathwayBeingMoved.vertexBeingModified === "last") {
        pathwayNode.vertex_index =
          this.pathwayBeingMoved.feature.getGeometry().getCoordinates().length -
          1;
        this.pathwayBeingMoved.endDisconnected = false;
      }

      this.updatedPathwayEndVertexCoordinates(pointCoords);
      let wkt = this.getModifiedObjectWKT();
      this.finishModifyingObject();

      let moveData = {
        uuid: this.moveObjectData.uuid,
        text_geom: wkt,
        projection: 3857,
        pathway_nodes: [pathwayNode],
      };
      this.movePathwayRequest(moveData);
    },

    movePathwayRequest(pathwayData) {
      if (this.pathwayBeingMoved.startDisconnected) {
        pathwayData.start_disconnected = true;
      }
      if (this.pathwayBeingMoved.endDisconnected) {
        pathwayData.end_disconnected = true;
      }
      this.moveObjectRequest(pathwayData);
      this.resetMovePathwayData();
    },

    checkIfPathwayEndDisconnects() {
      this.pathwayBeingMoved.startDisconnected =
        this.pathwayBeingMoved.vertexBeingModified === "first" &&
        this.pathwayBeingMoved.originalData &&
        this.pathwayBeingMoved.originalData.pw_node1_id !== null;
      this.pathwayBeingMoved.endDisconnected =
        this.pathwayBeingMoved.vertexBeingModified === "last" &&
        this.pathwayBeingMoved.originalData &&
        this.pathwayBeingMoved.originalData.pw_node2_id !== null;
    },

    getPointCoordsFromWKT(wkt) {
      return new WKT().readGeometry(wkt).getCoordinates();
    },

    getSelectedListObjectFeature() {
      let featureGeoJSON = this.selectedObjectIdentifiers.geojson;
      let features = new GeoJSON().readFeatures(featureGeoJSON, {
        featureProjection: "EPSG:3857",
      });
      return features[0];
    },

    getPathwaySnapCoordinates() {
      let selectedObjectCoords = this.selectedObjectIdentifiers.coordinates;
      let interceptionPoint = this.getPathwaysIntersectionCoordinate();

      let firstPathwayTipCoordinates = selectedObjectCoords[0];
      let distanceToFirstTip = this.getDistFromPathwayTipToIntersection(
        firstPathwayTipCoordinates,
        interceptionPoint
      );

      let LastPathwayTipCoordinates =
        selectedObjectCoords[selectedObjectCoords.length - 1];
      let distanceToLastTip = this.getDistFromPathwayTipToIntersection(
        LastPathwayTipCoordinates,
        interceptionPoint
      );

      let closestPathwayTipCoordinate;
      if (distanceToFirstTip <= distanceToLastTip) {
        closestPathwayTipCoordinate = firstPathwayTipCoordinates;
      } else {
        closestPathwayTipCoordinate = LastPathwayTipCoordinates;
      }
      return closestPathwayTipCoordinate;
    },

    getPathwaysIntersectionCoordinate() {
      let intersectionCoordinate = null;
      if (this.currentActionType === "move") {
        intersectionCoordinate = this.pathwayBeingMoved.coordinates;
      } else {
        intersectionCoordinate =
          this.pathwayVerticesCoordinates[
            this.pathwayVerticesCoordinates.length - 1
          ];
      }
      return this.getTransformedGeom(intersectionCoordinate);
    },

    getDistFromPathwayTipToIntersection(tipCoordinates, interceptionPoint) {
      let tip = this.getTransformedGeom(tipCoordinates);
      return this.calculateDistance(
        interceptionPoint.flatCoordinates,
        tip.flatCoordinates
      );
    },

    getTransformedGeom(coordinates) {
      if (coordinates) {
        let geom = new Point(coordinates);
        return geom.transform("EPSG:3857", "EPSG:4326");
      }
    },

    calculateDistance(coordinate1, coordinate2) {
      let from = point(coordinate1);
      let to = point(coordinate2);
      let options = { units: "kilometers" };
      let distance = require("@turf/distance/main");
      return distance(from, to, options);
    },

    updateGeoserver() {
      this.objectsImageSource.updateParams({ time: Date.now() });
    },

    updateProjection(projection) {
      const newProjection = getProjection("EPSG:" + projection);

      this.mousePositionControl.setProjection(newProjection);
      this.mousePositionControl.setCoordinateFormat((coordinates) => {
        let lon = coordinates[0];
        let lat = coordinates[1];

        if (projection == "4326") {
          lon = lon % 360;
          if (lon > 180) {
            lon = lon - 360;
          } else if (lon < -180) {
            lon = 360 + lon;
          }
        }

        let modifiedCoordinate = [lon, lat];

        let coordinatesFormat = "X: {x} Y: {y}";
        if (projection == "4326") {
          coordinatesFormat = "Lat: {y} Lon: {x}";
        }

        return format(modifiedCoordinate, coordinatesFormat, 8);
      });
    },

    getNodeWKT(e, callback) {
      let coordinates;
      if (e.hasOwnProperty("coordinate")) {
        coordinates = e.coordinate;
      } else {
        coordinates = e;
      }
      let feature = new Feature({ geometry: new Point(coordinates) });
      let wkt = this.getFeatureWKT(feature);
      callback(wkt);
    },

    drawNewLineString() {
      this.disableMapClickEvents();
      unByKey(this.singleClickEvent);

      this.newObjectDraw = new Draw({
        features: new Collection([]),
        type: "LineString",
        condition: (e) => {
          return e.pointerEvent.buttons === 1; // only allows drawing on leftclick (1),
        },
      });

      this.map.addInteraction(this.newObjectDraw);

      return this.newObjectDraw;
    },

    addNewLineStringNode(event) {
      if (this.isClickInsideWorkspace(event)) {
        this.getObjects(event, this.selectedToolObjectLayer);
      } else {
        this.newObjectDraw.removeLastPoint();
      }
    },

    drawNewPolygon(e, promise) {
      this.newObjectDraw = new Draw({
        features: new Collection([]),
        ...(this.currentActionType === "select" && {
          source: this.drawLayerSource,
        }),
        freehand: false,
        type: "Polygon",
      });
      let draw = this.newObjectDraw;

      this.map.addInteraction(draw);
      draw.startDrawing_(e);

      this.singleClickEvent = this.map.on("singleclick", (ev) => {
        if (!this.isClickInsideWorkspace(ev)) {
          draw.removeLastPoint();
        }
      });

      this.rightClickEvent = this.map.on("contextmenu", () =>
        draw.removeLastPoint()
      );

      promise(
        new Promise((resolve) => {
          draw.on("drawend", (e) => {
            let polygonWKT = this.getFeatureWKT(e.feature);
            this.map.removeInteraction(draw);
            resolve(polygonWKT);
          });
        })
      );
    },

    drawNewPathway(e) {
      this.lineStringWKT = [];
      this.pathwayVerticesCoordinates = [];
      this.draw = this.drawNewLineString();

      this.createPathwayTooltip();

      this.draw.on("drawstart", () => this.addVertexToNewPathway(e));

      this.draw.startDrawing_(e);

      let listener = this.draw.sketchFeature_.values_.geometry.on(
        "change",
        (e) => {
          let coords = e.target.getCoordinates();
          if (coords.length > 1) {
            this.updatePathwayTooltip(coords[coords.length - 1]);
          }
        }
      );

      this.clickEvent = this.map.on("click", (click) =>
        this.addVertexToNewPathway(click)
      );

      this.rightClickEvent = this.map.on("contextmenu", () => {
        this.draw.removeLastPoint();
        this.pathwayVerticesCoordinates.pop();
        if (this.pathwayVerticesCoordinates.length === 0) {
          this.createPathwayTooltip();
        }
      });

      this.draw.on("drawend", () => {
        this.map.removeOverlay(this.pathwayTooltip);
        this.finishPathwayDrawing();
      });
    },

    addVertexToNewPathway(e) {
      if (this.isClickInsideWorkspace(e)) {
        this.pathwayVerticesCoordinates.push(e.coordinate);
        this.getClickLocationData(e).then((locationData) => {
          if (locationData.numberReturned > 0) {
            this.listPotentialPathwayNodes(e, locationData.features);
            this.loading = false;
          }
        });
      } else {
        this.newObjectDraw.removeLastPoint();
        this.loading = false;
      }
    },

    updatePathwayTooltip(tooltipCoordinate) {
      if (
        this.draw &&
        this.draw.sketchFeature_ &&
        this.draw.sketchFeature_.values_
      ) {
        let feature = this.draw.sketchFeature_.values_.geometry;
        let length = this.formatMeasuredLengthOutput(feature);
        this.pathwayTooltipElement.innerHTML = length;
        this.pathwayTooltip.setPosition(tooltipCoordinate);
      }
    },

    createPathwayTooltip() {
      if (this.pathwayTooltipElement) {
        this.pathwayTooltipElement.parentNode.removeChild(
          this.pathwayTooltipElement
        );
      }
      this.pathwayTooltipElement = document.createElement("div");
      this.pathwayTooltipElement.className = "ol-tooltip ol-tooltip-measure";
      this.pathwayTooltip = new Overlay({
        element: this.pathwayTooltipElement,
        offset: [0, -15],
        positioning: "bottom-center",
      });
      this.map.addOverlay(this.pathwayTooltip);
    },

    listPotentialPathwayNodes(clickEvent, features) {
      this.objectList.list = this.filterObjectList(features, [
        { key: "objectType", value: "technical_point" },
        { key: "objectType", value: "technical_site" },
        { key: "objectType", value: "building" },
        { key: "objectType", value: "pathway" },
        { key: "objectType", value: "site" },
      ]);
      if (!this.isObjectListEmpty()) {
        this.includeNoneOptionInObjectList();
        this.objectList.open = true;
      } else {
        this.checkIfPathwayEndDisconnects();
      }
    },

    splitOtherPathwayWithCurrentPathway() {
      this.linkPathwayToPathway.dialog = false;
      this.linkPathwayToPathway.snap = false;
      this.linkPathwayToPathway.split = true;

      let intersectedPathwayUUID = {
        uuid: this.selectedObjectIdentifiers.uuid,
      };

      if (this.currentActionType === "create") {
        this.splitOtherPathwayWithNewPathway(intersectedPathwayUUID);
      } else if (this.currentActionType === "move") {
        this.splitOtherPathwayWithPathwayBeingMoved(intersectedPathwayUUID);
      }
    },

    splitOtherPathwayWithNewPathway(intersectedPathwayUUID) {
      if (this.linkPathwayToPathway.onFirstPoint) {
        this.linkPathwayToPathway.startPathwayData = intersectedPathwayUUID;
        this.linkPathwayToPathway.onFirstPoint = false;
        this.linkPathwayToPathway.split = false;
      } else {
        this.linkPathwayToPathway.endPathwayData = intersectedPathwayUUID;
        this.newObjectDraw.finishDrawing();
      }
    },

    splitOtherPathwayWithPathwayBeingMoved(intersectedPathwayUUID) {
      let wkt = this.getModifiedObjectWKT();
      this.finishModifyingObject();
      let moveData = {
        uuid: this.moveObjectData.uuid,
        text_geom: wkt,
        projection: 3857,
        pathway: {
          splitStart: this.pathwayBeingMoved.vertexBeingModified === "first",
          splitEnd: this.pathwayBeingMoved.vertexBeingModified === "last",
        },
      };

      if (moveData.pathway.splitStart) {
        moveData.pathway.start_uuid = intersectedPathwayUUID.uuid;
      } else if (moveData.pathway.splitEnd) {
        moveData.pathway.end_uuid = intersectedPathwayUUID.uuid;
      }
      this.checkIfPathwayEndDisconnects();

      this.movePathwayRequest(moveData);

      this.linkPathwayToPathway.split = false;
    },

    snapCurrentPathwayToOtherPathwayTip() {
      this.linkPathwayToPathway.dialog = false;
      this.linkPathwayToPathway.snap = true;
      this.linkPathwayToPathway.split = false;

      let coords = this.getPathwaySnapCoordinates();
      let pointCoords = [];
      this.getNodeWKT(coords, (pointWKT) => {
        pointCoords = this.getPointCoordsFromWKT(pointWKT);
      });
      if (this.currentActionType === "create") {
        this.pathwayVerticesCoordinates.pop();
        this.pathwayVerticesCoordinates.push(pointCoords);

        if (this.linkPathwayToPathway.onFirstPoint) {
          this.newObjectDraw.sketchCoords_[0] = coords;
          this.linkPathwayToPathway.snap = false;
        } else {
          let lastCoordIndex = this.newObjectDraw.sketchCoords_.length - 1;
          this.newObjectDraw.sketchCoords_[lastCoordIndex] = coords;
          this.newObjectDraw.finishDrawing();
        }
      } else if (this.currentActionType === "move") {
        this.updatedPathwayEndVertexCoordinates(pointCoords);
      }
    },

    updatedPathwayEndVertexCoordinates(pointCoords) {
      let feature = this.drawLayerSource.getFeatures()[0];
      this.drawLayerSource.clear();
      if (feature) {
        let geom = feature.getGeometry();
        let newCoordinates = geom.getCoordinates();
        if (
          this.pathwayBeingMoved &&
          this.pathwayBeingMoved.vertexBeingModified === "first"
        ) {
          newCoordinates[0] = pointCoords;
          this.pathwayBeingMoved.startDisconnected =
            this.pathwayBeingMoved.originalData &&
            this.pathwayBeingMoved.originalData.pw_node1_id !== null;
        } else if (
          this.pathwayBeingMoved &&
          this.pathwayBeingMoved.vertexBeingModified === "last"
        ) {
          newCoordinates[newCoordinates.length - 1] = pointCoords;
          this.pathwayBeingMoved.endDisconnected =
            this.pathwayBeingMoved.originalData &&
            this.pathwayBeingMoved.originalData.pw_node2_id !== null;
        }
        geom.setCoordinates(newCoordinates);
        feature.setGeometry(geom);
        this.drawLayerSource.addFeature(feature);
      }
    },

    finishPathwayDrawing() {
      if (this.pathwayVerticesCoordinates.length > 1) {
        let pathwayFeature = new GeoJSON().readFeature({
          type: "Feature",
          id: 2130,
          geometry: {
            type: "LineString",
            coordinates: this.pathwayVerticesCoordinates,
          },
        });
        this.lineStringWKT = new WKT().writeFeature(pathwayFeature);
        this.disableDraw();
        this.openForm(this.selectedToolObjectLayer, "create");
      } else {
        this.$toasted.global.toast_error(
          "A pathway must have more than 1 point"
        );
        this.$nextTick(() => {
          this.pathwayNodes = [];
          this.resetLineString();
        });
      }
    },

    cancelConnectCurrentPathwayWithPathway() {
      this.linkPathwayToPathway.dialog = false;
      this.linkPathwayToPathway.snap = false;
      this.linkPathwayToPathway.split = false;
      this.UPDATE_SELECTED_OBJECT_IDENTIFIERS({ none: true });
      this.linkPathwayToPathway.onFirstPoint = false;
    },

    getClickLocationData(clickEvent) {
      return new Promise((resolve) => {
        let url = this.getObjectsImageSourceURL(clickEvent.coordinate);

        this.loading = true;
        this.fetchFeaturesFromGeoServer({ data: url })
          .then((locationData) => {
            this.contextMenuPixel = clickEvent.pixel;
            this.contextMenuCoords = clickEvent.coordinate;

            resolve(locationData);
          })
          .catch((error) => console.error(error));
      });
    },

    drawNewFieldCable(e) {
      this.lineStringWKT = [];
      this.fieldCableNodes = [];

      this.drawNewLineString(e);
      this.newObjectDraw.startDrawing_(e);

      this.singleClickEvent = this.map.on("singleclick", (ev) =>
        this.addNewLineStringNode(ev)
      );

      this.rightClickEvent = this.map.on("contextmenu", () => {
        this.newObjectDraw.removeLastPoint();
        this.fieldCableNodes.pop();
      });

      this.newObjectDraw.on("drawend", (event) => {
        let techPoints = this.fieldCableNodes.filter(
          (node) => node.id !== null
        );

        if (techPoints.length > 1) {
          let fieldCableFeature = event.feature;
          this.lineStringWKT = this.getFeatureWKT(fieldCableFeature);

          this.disableDraw();
          this.openForm(this.selectedToolObjectLayer, "create");
        } else {
          this.$toasted.global.toast_error(
            "A cable line must have at least 2 poles!"
          );
          this.fieldCableNodes = [];
        }
      });
    },

    finishModifyingObject() {
      this.drawLayerSource.clear();
      this.modifyInteraction.setActive(false);
    },

    getFeatureWKT(feature) {
      if (feature) {
        return new WKT().writeFeature(feature);
      }
    },

    openFormFromObjectMenu(action) {
      this.closeObjectMenu();
      this.openForm(this.selectedObjectIdentifiers.layerName, action);
    },

    openDetailFormFromObjectMenu() {
      this.closeObjectMenu();
      this.openForm("Detail", "edit");
    },
    refresh(layer, action) {
      this.fetchObjectData().then((objectData) => {
        this.UPDATE_SELECTED_OBJECT_DATA(objectData);
        this.openForm(layer, action);
      });
    },
    openChangeLogForm() {
      this.closeObjectMenu();
      this.openForm("ChangeLog", "edit");
    },

    closeObjectMenus() {
      this.clickType = "";
      this.closeObjectList();
      this.closeObjectMenu();
      this.contextMenuPixel = [];
      this.contextMenuCoords = [];
    },

    closeObjectMenu() {
      this.$nextTick(() => {
        this.removeHighlightFromObjectOnLeave();
      });
      this.objectMenu.open = false;
    },

    isLayerActive(objectLayer) {
      let activeLayers = this.getActiveLayersNamesList(
        JSON.parse(sessionStorage.getItem("active_layers"))
      );

      if (objectLayer.includes("local")) {
        let technicalLocalLayerIndex = activeLayers.findIndex((item) =>
          item.includes("local")
        );
        return technicalLocalLayerIndex !== -1;
      }

      return activeLayers.includes(objectLayer);
    },

    activateLayer() {
      let objectType =
        this.selectedToolObjectLayer !== ""
          ? this.selectedToolObjectLayer
          : "field_workorders";
      let layer = this.findLayer(objectType);
      let activeLayers = JSON.parse(sessionStorage.getItem("active_layers"));
      activeLayers = activeLayers.concat(layer);
      sessionStorage.setItem("active_layers", JSON.stringify(activeLayers));
      this.updateGeoserverLayers();
      this.openDialogBoxActiveLayer = false;
      bus.$emit("activeLayer");
    },

    findLayer(layerId) {
      let flatLayers = _.flatMapDeep(this.allLayers, this.getMembers);
      if (layerId === "fc_technical_local") {
        return flatLayers.filter(
          (layer) =>
            layer.layers.length &&
            layer.layers[0] &&
            layer.layers[0].layer &&
            layer.layers[0].layer.layer.includes(layerId)
        );
      }
      return flatLayers.filter(
        (layer) =>
          layer.layers.length &&
          layer.layers[0] &&
          layer.layers[0].layer &&
          layer.layers[0].layer.layer === layerId
      );
    },

    getMembers(member) {
      if (!member.children || !member.children.length) {
        return member;
      }
      return [member, _.flatMapDeep(member.children, this.getMembers)];
    },

    createObject(e, objectLayer) {
      switch (objectLayer) {
        case "fc_pole":
        case "fc_manhole":
        case "fc_cabinet":
        case "fc_toby_box":
        case "fc_virtual":
        case "fc_optical_line_termination":
        case "fc_fiber_distribution_point":
        case "fc_building":
        case "fc_site":
          this.getObjects(e, objectLayer).then(() => {
            if (this.isObjectListEmpty()) {
              this.openForm(this.selectedToolObjectLayer, "create");
            }
          });
          this.getNodeWKT(e, (result) => {
            this.wkt = result;
          });
          break;

        case "fc_pathway":
          this.drawNewPathway(e);
          break;

        case "fc_closure":
          this.getObjects(e, objectLayer).then(() => {
            this.objectList.open = false;
            if (this.isObjectListEmpty()) {
              this.closureWithoutTechPoint.dialog = true;
              this.closureWithoutTechPoint.event = e;
            } else if (this.objectList.list.length === 1) {
              this.UPDATE_SELECTED_OBJECT_IDENTIFIERS(this.objectList.list[0]);
              this.continueCreatingOrMovingProcess();
            }
          });
          this.getNodeWKT(e, (result) => {
            this.wkt = result;
          });
          break;

        case "fc_technical_local":
          this.getObjects(e, objectLayer);
          this.getNodeWKT(e, (result) => {
            this.wkt = result;
          });
          break;

        case "fc_cable":
        case "fc_microduct_bundle":
          this.startCable();
          break;

        case "fc_field_cable":
          this.drawNewFieldCable(e);
          break;

        case "fc_slack":
          this.getObjects(e, objectLayer);
          this.getNodeWKT(e, (result) => {
            this.wkt = result;
          });
          break;

        case "fc_blockage":
          this.getObjects(e, objectLayer).then(() => {
            if (this.isObjectListEmpty()) {
              this.resetBlockageData()
              this.openForm(this.selectedToolObjectLayer, "create");
            }
          });
          this.getNodeWKT(e, (result) => {
            this.wkt = result;
          });
          break;

        case "fc_fiber_terminal_box":
          this.startCluster(e);
          break;
        case "fc_cluster_tool":
          this.startCluster(e);
          break;

        case "fc_fdp_zone":
          this.mapEvent = e;
          this.getObjects(e, objectLayer);
          break;
        case "fc_olt_zone":
          this.mapEvent = e;
          this.getObjects(e, objectLayer);
          break;
        case "fc_closure_zone":
          this.mapEvent = e;
          this.getObjects(e, objectLayer);
          break;
        case "fc_annotation_zone":
        case "fc_asset_reservation":
        case "external_import":
          this.startArea(e);
          break;

        case "fc_tower":
        case "fc_annotation_point":
          this.getNodeWKT(e, (result) => {
            this.wkt = result;
          });
          this.openForm(objectLayer, "create");
          break;

        case "fc_bulk_tp":
          this.drawNewBulkTPLine(e);
          break;
      }
    },

    isObjectListEmpty() {
      return this.objectList.list.length === 0;
    },

    enableObjectCreation() {
      unByKey(this.rightClickEvent);
    },

    enableCableCreation() {
      this.enableObjectCreation();
      this.cable.creating = true;
    },

    startCable() {
      this.disableMapClickEvents();
      unByKey(this.singleClickEvent);
      this.lineStringWKT = [];
      this.singleClickEvent = this.map.on("singleclick", (ev) => {
        this.getObjects(ev, this.selectedToolObjectLayer);
      });
    },

    enableClusterCreation() {
      this.enableObjectCreation();
      this.cluster.creating = true;
    },
    enableClusterToolCreation() {
      this.enableObjectCreation();
      this.clusterTool.creating = true;
      this.$store.commit("SET_CLUSTER_TOOL_FORM", true);
    },

    /**
     * Opens the bulk creation form
     */
    enableBulkTPCreation() {
      this.bulk_tp.creating = true;
    },

    /**
     * Opens the bundle creation form
     */
    enableBundleCreation() {
      this.bundle.creating = true;
    },

    /**
     * Opens the external import form
     */
    enableExternalImport() {
      this.externalImport.open = true;
    },

    startCluster(e) {
      this.disableMapClickEvents();
      unByKey(this.singleClickEvent);
      if (this.cluster.type === "point" || this.clusterTool.type === "point") {
        this.singleClickEvent = this.map.on("singleclick", (ev) => {
          this.getObjects(ev, this.selectedToolObjectLayer);
        });
      } else if (
        this.cluster.type === "polygon" ||
        this.clusterTool.type === "polygon"
      ) {
        bus.$emit("start-cluster-by-polygon", e);
      } else {
        this.mapClickListener();
        bus.$emit("highlight-cluster-box");
      }
    },

    createCable() {
      if (this.lineStringWKT.length > 1 && this.cable.creatable) {
        this.openForm("fc_cable", "create");
      }
    },

    createArea(dataSaveArea, isMeasurementArea = null) {
      this.resetArea(true);
      this.wkt = dataSaveArea.text_geom;

      if (this.selectedToolObjectLayer === "fc_asset_reservation") {
        let validLayers =
          this.$store.getters.getValidAssetReservationAssetLayers;
        this.getPossibleAssetReservationObjectLayers(validLayers);
      } else if (this.selectedToolObjectLayer === "external_import") {
        this.$toasted.global.toast_success(this.$i18n.t("Success"));
      } else {
        this.openForm(
          this.selectedToolObjectLayer,
          "create",
          isMeasurementArea
        );
      }
    },

    getPossibleAssetReservationObjectLayers(validLayers) {
      return new Promise((resolve) => {
        let activeLayers = this.getActiveLayersNamesList(
          JSON.parse(sessionStorage.getItem("active_layers"))
        );

        let layersToSend = activeLayers.filter((value) =>
          validLayers.includes(value)
        );

        this.loading = true;
        this.GET_OBJECTS_LAYERS_INSIDE({
          text_geom: this.wkt,
          projection: 3857,
          layers: layersToSend,
        })
          .then((layers) => {
            if (layers.length > 0) {
              layers = layers.map((item) => item.layer);
              this.setValidAssetReservationObjectLayers(layers);
              this.openForm(
                this.selectedToolObjectLayer,
                this.currentActionType
              );
            } else {
              this.$toasted.global.toast_error(
                this.$i18n.t("AssetAreaWithoutValidAssets")
              );
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .then(() => {
            this.loading = false;
            resolve();
          });
      });
    },

    addObjectsToReservation(e, validLayers) {
      this.disableMapClickEvents();
      this.resetArea(false);

      this.drawNewPolygon(e, (promise) => {
        promise.then((result) => {
          this.wkt = result;
          this.getPossibleAssetReservationObjectLayers(validLayers).then(() => {
            this.SET_TOOL_ACTION("");
            this.SET_TOOL_OBJECT_LAYER("");
          });
        });
      });
    },

    moveObject() {
      this.disableMapClickEvents();

      this.SET_TOOL_ACTION("move");
      this.SET_TOOL_OBJECT_LAYER(this.selectedObjectIdentifiers.layerName);

      this.moveObjectData = {
        endpoint: this.selectedObjectIdentifiers.endpoint,
        uuid: this.selectedObjectIdentifiers.uuid,
      };

      switch (this.selectedObjectIdentifiers.endpoint) {
        case "pathways":
          this.movePathway();
          break;
        case "annotationszones":
        case "closureszones":
        case "opticallineterminations":
        case "fiberdistributionpoints":
          this.moveArea();
          break;
        default:
          this.movePointObject();
          break;
      }
    },

    movePathway() {
      this.fetchObjectData().then((objectData) => {
        this.pathwayBeingMoved.originalData = objectData;
        this.modifyLineString().then((res) => {
          this.wkt = res;
          let moveData = {
            uuid: this.moveObjectData.uuid,
            text_geom: this.wkt,
            projection: 3857,
          };

          this.movePathwayRequest(moveData);
        });
      });
    },

    modifyLineString() {
      this.startModifyingObject();

      this.modifyInteraction.on("modifystart", () => {
        this.updateFeatureOfPathwayBeingMoved();
      });

      this.modifyInteraction.on("modifyend", (moveEvent) => {
        let mapEvent = moveEvent.mapBrowserEvent;
        if (this.isClickInsideWorkspace(mapEvent)) {
          this.editingObjectType = "pathway";
          this.pathwayBeingMoved.coordinates = mapEvent.coordinate;
          this.getPathwayVertexPositionBeingModified();

          if (this.pathwayBeingMoved.vertexBeingModified !== null) {
            this.getClickLocationData(mapEvent).then((locationData) => {
              if (locationData.numberReturned > 0) {
                this.listPotentialPathwayNodes(mapEvent, locationData.features);
                this.loading = false;
              } else {
                this.checkIfPathwayEndDisconnects();
                this.loading = false;
              }
            });
          }
        } else {
          this.drawLayerSource.clear();
          this.drawLayerSource.addFeature(this.pathwayBeingMoved.feature);
        }
      });

      return new Promise((resolve) => {
        this.doubleClickEvent = this.map.once("dblclick", () => {
          let wkt = this.getModifiedObjectWKT();
          this.finishModifyingObject();
          resolve(wkt);
        });
      });
    },

    getPathwayVertexPositionBeingModified() {
      let position = null;
      let initialCoords =
        this.pathwayBeingMoved.feature.getGeometry().flatCoordinates;
      let finalCoords = this.drawLayerSource
        .getFeatures()[0]
        .getGeometry().flatCoordinates;
      if (initialCoords.length === finalCoords.length) {
        if (initialCoords[0] !== finalCoords[0]) {
          position = "first";
        } else {
          let lastVertexIndex = initialCoords.length - 1;
          if (initialCoords[lastVertexIndex] !== finalCoords[lastVertexIndex]) {
            position = "last";
          }
        }
      }
      this.pathwayBeingMoved.vertexBeingModified = position;
    },

    updateFeatureOfPathwayBeingMoved() {
      this.pathwayBeingMoved.feature = this.drawLayerSource
        .getFeatures()[0]
        .clone();
    },

    moveArea() {
      this.modifyPolygon().then((res) => {
        this.wkt = res;

        this.moveObjectRequest({
          uuid: this.moveObjectData.uuid,
          text_geom: this.wkt,
          projection: 3857,
        });
      });
    },

    modifyPolygon() {
      this.startModifyingObject();

      return new Promise((resolve) => {
        this.doubleClickEvent = this.map.once("dblclick", () => {
          let wkt = this.getModifiedObjectWKT();
          this.finishModifyingObject();
          resolve(wkt);
        });
      });
    },

    movePointObject() {
      this.singleClickEvent = this.map.on("singleclick", (e) => {
        if (this.isClickInsideWorkspace(e)) {
          this.getNodeWKT(e, (result) => {
            this.wkt = result;
          });

          if (this.moveObjectData.endpoint === "annotationspoints") {
            this.moveObjectRequest({
              uuid: this.moveObjectData.uuid,
              text_geom: this.wkt,
              projection: 3857,
            });
          } else {
            this.getClickLocationData(e).then((locationData) => {
              let updatedObjectData = this.getUpdatedPointObjectData();
              if (locationData.numberReturned === 0) {
                this.moveObjectRequest(updatedObjectData);
              } else if (locationData.numberReturned > 0) {
                this.prepareObjectList(
                  locationData.features,
                  this.selectedToolObjectLayer
                );

                if (this.isObjectListEmpty()) {
                  if (this.moveObjectData.endpoint === "closures") {
                    this.$toasted.global.toast_error(
                      this.$i18n.t("NoTechnicalPointDetected")
                    );
                    this.loading = false;
                  } else {
                    this.moveObjectRequest(updatedObjectData);
                  }
                }
              }
            });
          }
        }
      });
    },

    getUpdatedPointObjectData() {
      let updatedObjectData = { uuid: this.moveObjectData.uuid };

      let node = { text_geom: this.wkt, projection: 3857 };
      if (this.linkObjectToPathway.split) {
        node.pathway = this.linkObjectToPathway.pathway;
        node.delete_pathways_association = "false";
      }

      if (
        this.moveObjectData.endpoint === "buildings" ||
        this.moveObjectData.endpoint === "sites"
      ) {
        updatedObjectData.technical_site = { node: node };
      } else {
        updatedObjectData.node = node;
      }

      return updatedObjectData;
    },

    moveClosure() {
      let updatedObjectData = {
        uuid: this.moveObjectData.uuid,
        technical_point: { uuid: this.selectedObjectIdentifiers.uuid },
      };
      this.moveObjectRequest(updatedObjectData).catch((error) => {
        this.$toasted.global.toast_error(error.response.data.data);
      });
    },

    startModifyingObject() {
      let feature = this.getSelectedListObjectFeature();
      this.drawLayerSource.addFeature(feature);
      this.modifyInteraction.setActive(true);
    },

    getModifiedObjectWKT() {
      let feature = this.drawLayerSource.getFeatures()[0];
      return this.getFeatureWKT(feature);
    },

    moveObjectRequest(moveData) {
      return new Promise((resolve, reject) => {
        this.loading = true;
        this.EDIT_OBJECTS({
          endpoint: this.moveObjectData.endpoint,
          data: [moveData],
        })
          .then(() => {
            resolve();

            this.moveObjectRequestSuccess();
          })
          .catch((error) => {
            console.error(error);

            reject(error);
          })
          .then(() => {
            this.loading = false;
            this.resetSelectedObjectLayer();
            this.SET_TOOL_ACTION("");
          });
      });
    },

    moveObjectRequestSuccess() {
      this.updateGeoserver();
      this.$toasted.global.toast_success(this.$i18n.t("MoveSuccess"));
    },

    fillSplitDataOnMoveObject(moveData) {
      return moveData;
    },

    startArea(e) {
      this.disableMapClickEvents();
      this.resetArea(false);

      this.drawNewPolygon(e, (promise) => {
        promise.then((result) => {
          this.createArea({
            text_geom: result,
            projection: 3857,
          });
        });
      });
    },

    changeLocalMapStyle(style) {
      let layers = this.map.getLayers();
      let source = layers.array_[0].values_.source;

      if (style === "dark-map") {
        source.setUrl(
          "https://{a-c}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
        );
        this.isDarkMap = true;
      } else if (style === "light-map") {
        source.setUrl(
          "https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
        );
        this.isDarkMap = false;
      }
    },

    printMap(data) {
      let dims = {
        a0: [1189, 841],
        a1: [841, 594],
        a2: [594, 420],
        a3: [420, 297],
        a4: [297, 210],
        a5: [210, 148],
      };

      document.body.style.cursor = "progress";
      let exportOptions = {
        filter(element) {
          return element.className.indexOf("ol-control") === -1;
        },
      };

      let format = data.format;
      let dim = dims[format];
      let size = this.map.getSize();
      let viewResolution = this.mapView.getResolution();

      this.map.once("rendercomplete", function () {
        exportOptions.width = size[0];
        exportOptions.height = size[1];
        toJpeg(this.map.getViewport(), exportOptions).then(function (dataUrl) {
          let pdf = new jsPDF("landscape", undefined, format);
          pdf.addImage(dataUrl, "JPEG", 0, 0, dim[0], dim[1]);
          pdf.save("map2.pdf");
          // Reset original map size
          this.map.setSize(size);
          this.mapView.setResolution(viewResolution);
          // exportButton.disabled = false;
          document.body.style.cursor = "auto";
          bus.$emit("close-print-form");
        });
      });

      let printSize = [size[0], size[1]];
      this.map.setSize(printSize);
    },

    endCableCreation() {
      this.resetCable();
      this.mapClickListener();
      this.updateGeoserver();
      this.cable.creating = true;
    },

    endBundleCreation() {
      this.closeBundle();
      this.mapClickListener();
      this.updateGeoserver();
    },

    closeCable() {
      this.resetCable();
      this.cable.creatable = false;
      this.cable.creating = false;
      this.resetCable();
      this.disableDraw();
      this.resetSelectedObjectLayer();
    },

    resetCable() {
      this.lineStringWKT = [];
      this.drawLayerSource.clear();
    },

    resetLineString() {
      this.lineStringWKT = [];
      this.pathwayVerticesCoordinates = [];
      this.fieldCableNodes = [];

      this.disableDraw();
      this.resetMapClickEvents();
    },

    resetPathwayData() {
      this.linkObjectToPathway.split = false;
      this.linkObjectToPathway.pathway.uuid = null;

      this.linkPathwayToPathway.snap = false;
      this.linkPathwayToPathway.split = false;
      this.linkPathwayToPathway.startPathwayData = null;
      this.linkPathwayToPathway.endPathwayData = null;

      this.pathwayNodes = [];
      this.pathwayVerticesCoordinates = [];
    },

    resetMovePathwayData() {
      this.resetPathwayData();
      this.pathwayBeingMoved = {
        feature: null,
        coordinates: null,
        vertexBeingModified: null,
        startDisconnected: false,
        endDisconnected: false,
        originalData: null,
      };
    },

    closeCluster() {
      this.resetCluster(true);
      this.cluster.creating = false;
      this.cluster.type = null;
      this.resetSelectedObjectLayer();
    },
    //closes menu and clears all data
    closeClusterTool() {
      this.resetClusterTool(true);
      this.clusterTool.creating = false;
      this.clusterTool.type = null;
      this.resetSelectedObjectLayer();
    },

    /**
     * Closes the bulk creation form
     */
    closeBulkTP() {
      this.bulk_tp.pathwayData = null;
      this.bulk_tp.techPointData = null;
      this.bulk_tp.numberOfTPs = null;
      this.bulk_tp.creating = false;
      this.bulk_tp.tooManyPointsDrawn = false;
      this.drawLayerSource.clear();
      this.bulkFeature = null;
      this.bulkTPLineVerticesCoordinates = [];
      this.techPointsCoordinates = [];
      this.resetSelectedObjectLayer();
      this.nextForm = null;
    },

    /**
     * Closes the bulk creation form
     */
    closeBundle() {
      this.bundle.creating = false;
      this.bundle.creatable = false;
      this.bundle.pathways = [];
      this.drawLayerSource.clear();
      this.resetSelectedObjectLayer();
      this.nextForm = null;
      this.lineStringWKT = [];
    },

    closeExternalImportWindow() {
      this.externalImport.open = false;
      this.resetSelectedObjectLayer();
      this.nextForm = null;
      this.wkt = null;
    },

    resetCluster(unbindEvent) {
      this.clusterWKT = [];
      this.disableDraw();

      if (unbindEvent) {
        this.cluster.creatable = false;
        this.mapClickListener();
      }
    },
    // disables draw when window is closed
    resetClusterTool(unbindEvent) {
      this.clusterToolWKT = [];
      this.disableDraw();

      if (unbindEvent) {
        this.clusterTool.creatable = false;
        this.mapClickListener();
      }
    },

    /**
     * Resets the bulk creation
     */
    resetBulkTP(unbindEvent) {
      this.lineStringWKT = [];
      this.disableDraw();

      if (unbindEvent) {
        this.mapClickListener();
      }
    },

    resetArea(unbindEvent) {
      this.disableDraw();

      if (unbindEvent) {
        unByKey(this.doubleClickEvent);
        this.mapClickListener();
      }
    },

    escKeyAction(event) {
      if (event.key === "Escape") {
        if (this.objectMenu.open) {
          this.closeObjectMenu();
        } else if (this.objectList.open) {
          this.closeObjectList();
        } else if (this.selectedToolObjectLayer !== "") {
          if (this.currentActionType === "create") {
            if (this.cable.creating) {
              bus.$emit("escape-cable-window");
            } else if (this.cluster.creating) {
              if (this.newObjectIsBeingDrawn()) {
                this.resetArea(true);
              } else {
                bus.$emit("escape-cluster-window");
              }
            } else if (this.newObjectIsBeingDrawn()) {
              this.newObjectDraw.abortDrawing_();
              this.lineStringWKT = [];
              this.pathwayNodes = [];
              this.pathwayVerticesCoordinates = [];
              this.fieldCableNodes = [];
            } else {
              bus.$emit("deselect-selected-object-to-create");
            }
          } else if (this.currentActionType === "move") {
            if (this.moveObjectData.endpoint.includes("pathways")) {
              this.resetMovePathwayData();
            }
            this.resetMove();
          } else if (this.currentActionType === "addAssetsToReservation") {
            if (this.newObjectIsBeingDrawn()) {
              this.newObjectDraw.abortDrawing_();
            } else {
              this.SET_TOOL_ACTION("");
              this.SET_TOOL_OBJECT_LAYER("");
            }
          }
        } else if (this.selectedMeasurementTool !== "") {
          if (this.sketch !== null) {
            this.measurementsDraw.abortDrawing_();
            this.sketch = null;
            this.map.removeInteraction(this.measurementsDraw);
          } else {
            this.setSelectedMeasureTool("");
          }
        } else if (this.showMeasurementsToolbar) {
          if (this.measurementsLayerSource.getFeatures().length > 0) {
            this.clearMeasurements();
          } else {
            this.setMeasurementsToolbarState(false);
            this.setOpenHomeSidebar("");
          }
        } else if (
          this.currentActionType === "select" ||
          this.currentActionType === "assetReservation" ||
          this.currentActionType === "workOrder"
        ) {
          this.resetMultiSelect();
        } else if (
          this.currentActionType !== "" ||
          this.currentlyOpenSidebar !== ""
        ) {
          this.SET_TOOL_ACTION("");
          this.setOpenHomeSidebar("");
        } else if (this.toolsSidebarIsOpen) {
          this.setHomeToolsSidebarState(false);
        }
      }
    },

    newObjectIsBeingDrawn() {
      return (
        this.newObjectDraw &&
        this.newObjectDraw.getActive() &&
        this.newObjectDraw.sketchFeature_ &&
        this.newObjectDraw.sketchFeature_.getGeometry()
      );
    },

    isClickInsideWorkspace(e) {
      if (
        (this.isAdmin ||
          this.selectedToolObjectLayer === "fc_annotation_zone") &&
        !this.forceWorkspaceFilter
      ) {
        return true;
      }

      let workspace = JSON.parse(this.$store.getters.workspace.geo_json);
      let coordinate = this.getTransformedGeom(e.coordinate);
      let object = {
        type: "Point",
        coordinates: coordinate.flatCoordinates,
      };

      let isInside = booleanContains(workspace, object);
      if (!isInside) {
        this.$toasted.global.toast_error(
          "You can't work outside of the workspace"
        );
      }
      return isInside;
    },

    startSelectionMultipleObjects(e) {
      this.disableMapClickEvents();
      this.resetArea(false);
      this.drawNewPolygon(e, (promise) => {
        promise.then((polygonWKT) => {
          if (this.currentActionType === "select") {
            this.prepareDataToSelectMultipleObjects(polygonWKT, 3857);
          } else if (this.currentActionType === "workOrder") {
            this.prepareDataToSelectWorkOrderObjects(polygonWKT, 3857);
          }
          this.resetArea(true);
        });
      });
    },

    prepareDataToSelectMultipleObjects(polygonWKT, projection) {
      this.requestSelectionOfMultipleObjects({
        text_geom: polygonWKT,
        projection: projection,
        layers: this.getActiveLayersNamesList(
          JSON.parse(sessionStorage.getItem("active_layers"))
        ),
      });
      localStorage.setItem("multi_select_geom", polygonWKT);
    },
    /**
     * @returns - layers, geom and projection of the selected area.
     */
    prepareDataToSelectWorkOrderObjects(polygonWKT, projection) {
      this.requestSelection({
        text_geom: polygonWKT,
        projection: projection,
        layers: this.getActiveLayersNamesList(
          JSON.parse(sessionStorage.getItem("active_layers"))
        ),
      });
    },

    async requestSelection(objectToSend) {
      this.loading = true;
      this.GET_OBJECTS_LAYERS_INSIDE(objectToSend)
        .then((layers) => {
          if (layers.length > 0) {
            this.workorders.workOrderOpen = true;
            this.resetMapClickEvents();
          } else {
            this.$toasted.global.toast_error(
              "No objects were found in the active layers."
            );
          }
          this.objectMenu.open = false;
        })
        .catch((error) => console.error(error))
        .then(() => {
          this.loading = false;
        });
    },

    requestSelectionOfMultipleObjects(objectToSend) {
      this.loading = true;
      this.GET_OBJECTS_LAYERS_INSIDE(objectToSend)
        .then((layers) => {
          if (layers.length > 0) {
            this.openMultiselectInNewTab();
            this.resetMapClickEvents();
          } else {
            this.drawLayerSource.clear();
            this.map.removeInteraction(this.newObjectDraw);
            this.$toasted.global.toast_error(
              "No objects were found in the active layers."
            );
          }
          this.objectMenu.open = false;
        })
        .catch((error) => console.error(error))
        .then(() => {
          this.loading = false;
        });
    },

    openMultiselectInNewTab() {
      let multiObjectSelectionData = this.$store.getters.getMultiObjectSelection;
      let multiObjectData = {
        layers: multiObjectSelectionData.layers,
        text_geom: multiObjectSelectionData.polygon,
        projection: multiObjectSelectionData.projection,
      };
      window.localStorage.setItem('multiObjectSelection', JSON.stringify(multiObjectData));
      const routeData = this.$router.resolve({ name: 'multiselect', params: { workspaceUUID: this.$route.params.workspace_uuid }});
      window.open(routeData.href, '_blank');
    },

    addMarkPoint(feature) {
      let geom = feature.getGeometry();
      let geometryType = geom.getType();
      let coordinates = geom.flatCoordinates;
      switch (geometryType) {
        case "Point":
          this.setMarkPointPosition(coordinates);
          break;

        case "Polygon":
        case "MultiPolygon":
          this.setPolygonMarkPoint(coordinates);
          break;

        case "LineString":
          this.setLineStringMarkPoints(geom);
          break;

        case "MultiLineString":
          if (this.isTrulyMultiLineString(geom)) {
            coordinates = this.getMultiLineEndCoordinates(geom);
            if (coordinates.length === 2) {
              this.setMarkPointPosition(coordinates[0]);
              this.setMarkPointPosition(coordinates[1]);
            }
          } else {
            this.setLineStringMarkPoints(geom);
          }

          break;
      }
    },

    isTrulyMultiLineString(geom) {
      let lineStrings = geom.getLineStrings();
      let nrOfLineStrings = lineStrings.length;
      return nrOfLineStrings > 1;
    },

    setPolygonMarkPoint(coordinates) {
      let polygonCoords = [];
      for (let i = 0; i < coordinates.length; i = i + 2) {
        polygonCoords.push([coordinates[i], coordinates[i + 1]]);
      }
      let turf = require("@turf/turf");
      let polygon = turf.polygon([polygonCoords]);
      let center = turf.centerOfMass(polygon);
      center = center.geometry.coordinates;
      this.setMarkPointPosition(center);
    },

    setLineStringMarkPoints(geom) {
      this.setMarkPointPosition(geom.getFirstCoordinate());
      this.setMarkPointPosition(geom.getLastCoordinate());
    },

    getMultiLineEndCoordinates(geom) {
      let lineStrings = geom.getLineStrings();
      let lineStringCoordinates = [];
      for (let lineString of lineStrings) {
        lineStringCoordinates.push(lineString.getFirstCoordinate());
        lineStringCoordinates.push(lineString.getLastCoordinate());
      }

      let finalCoordinates = [];
      lineStringCoordinates.forEach((coordinate, i) => {
        if (this.arrayIsUnique(coordinate, i, lineStringCoordinates)) {
          finalCoordinates.push(coordinate);
        }
      });
      return finalCoordinates;
    },

    arrayIsUnique(value, index, array) {
      let duplicate;
      array.forEach((comparisonValue, i) => {
        if (index !== i && this.arraysAreEqual(value, comparisonValue)) {
          duplicate = comparisonValue;
        }
      });
      return !duplicate;
    },

    arraysAreEqual(arrayA, arrayB) {
      return JSON.stringify(arrayA) === JSON.stringify(arrayB);
    },

    setMarkPointPosition(coordinates) {
      let x = coordinates[0];
      let y = coordinates[1];
      let feature = this.markPointFeature.clone();
      feature.getGeometry().setCoordinates([x, y]);
      this.highlightLayerSource.addFeature(feature);
    },

    resetMultiSelect() {
      if (this.newObjectIsBeingDrawn()) {
        this.resetArea(true);
      } else {
        this.resetMapClickEvents();
        this.SET_TOOL_ACTION("");
        this.setOpenHomeSidebar("");
      }
    },

    resetMove() {
      this.drawLayerSource.clear();
      this.modifyInteraction.setActive(false);

      this.SET_TOOL_ACTION("");
      this.resetSelectedObjectLayer();
    },

    resetSelectedObjectLayer() {
      this.SET_TOOL_OBJECT_LAYER("");
      this.resetMapClickEvents();
    },

    ///////////////////////////////
    /* Measurement Tools methods */
    ///////////////////////////////

    addMeasurementInteraction() {
      this.disableMapClickEvents();
      unByKey(this.singleClickEvent);

      this.measurementsDraw = this.updateMeasurementDrawLayer();
      this.map.addInteraction(this.measurementsDraw);

      this.createMeasureTooltip();

      let listener;
      this.measurementsDraw.on("drawstart", (evt) =>
        this.onMeasurementDrawStart(listener, evt)
      );
      this.measurementsDraw.on("drawend", () =>
        this.onMeasurementDrawEnd(listener)
      );
    },

    updateMeasurementDrawLayer() {
      let type = "";
      switch (this.selectedMeasurementTool) {
        case "length":
        case "angle":
          type = "LineString";
          break;
        case "area":
          type = "Polygon";
          break;
      }
      return new Draw({
        source: this.measurementsLayerSource,
        type: type,
        style: new Style({
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
          }),
          stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.5)",
            lineDash: [10, 10],
            width: 2,
          }),
          image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
              color: "rgba(0, 0, 0, 0.7)",
            }),
            fill: new Fill({
              color: "rgba(255, 255, 255, 0.2)",
            }),
          }),
        }),
      });
    },

    onMeasurementDrawStart(listener, evt) {
      this.sketch = evt.feature;
      let tooltipCoordinate = evt.coordinate;
      listener = this.sketch.getGeometry().on("change", (evt) => {
        let geom = evt.target;
        this.updateMeasurementTooltip(geom, tooltipCoordinate);
        this.manageAngleMeasurementCreation(geom);
      });
    },

    updateMeasurementTooltip(geom, tooltipCoordinate) {
      let output;
      if (geom instanceof Polygon) {
        output = this.formatMeasuredAreaOutput(geom);
        tooltipCoordinate = geom.getInteriorPoint().getCoordinates();
      } else if (geom instanceof LineString) {
        if (this.selectedMeasurementTool === "length") {
          output = this.formatMeasuredLengthOutput(geom);
          tooltipCoordinate = geom.getLastCoordinate();
        } else if (this.selectedMeasurementTool === "angle") {
          output = this.formatMeasuredAngleOutput(geom);
          tooltipCoordinate = geom.getLastCoordinate();
        }
      }
      this.measureTooltipElement.innerHTML = output;
      this.measureTooltipElement.id = "tooltip-" + geom.ol_uid;
      this.measureTooltip.setPosition(tooltipCoordinate);
    },

    /**
     * Format length output.
     * @param {LineString} line The line.
     * @return {string} The formatted length.
     */
    formatMeasuredLengthOutput(line) {
      let output;
      let length = getLength(line);
      if (length > 100) {
        output = Math.round((length / 1000) * 100) / 100 + " km";
      } else {
        output = Math.round(length * 100) / 100 + " m";
      }
      return output;

    },

    /**
     * Format angle output.
     * @param {LineString} line The line.
     * @return {string} The formatted angle.
     */
    formatMeasuredAngleOutput(line) {
      let startCoordinate = line.getCoordinateAt(0);
      startCoordinate = toLonLat(startCoordinate);
      let endCoordinate = line.getCoordinateAt(1);
      endCoordinate = toLonLat(endCoordinate);

      let turf = require("@turf/turf");
      let bearing = turf.bearing(startCoordinate, endCoordinate);
      let output = bearingToAzimuth(bearing).toFixed(2);
      return output + " º";

    },

    /**
     * Format area output.
     * @param {Polygon} polygon The polygon.
     * @return {string} Formatted area.
     */
    formatMeasuredAreaOutput(polygon) {
      let output;
      let area = getArea(polygon);
      if (area > 10000) {
        output = Math.round((area / 1000000) * 100) / 100 + " km<sup>2</sup>";
      } else {
        output = Math.round(area * 100) / 100 + " m<sup>2</sup>";
      }
      return output;
    },

    manageAngleMeasurementCreation(geom) {
      if (this.selectedMeasurementTool === "angle") {
        let hasOneSegment = geom.getCoordinates().length > 2;
        if (hasOneSegment) {
          this.measurementsDraw.finishDrawing();
        }
      }
    },

    onMeasurementDrawEnd(listener) {
      this.measureTooltipElement.className = "ol-tooltip ol-tooltip-static";
      this.measureTooltip.setOffset([0, -7]);
      this.sketch = null;
      // unset tooltip so that a new one can be created
      this.measureTooltipElement = null;
      this.createMeasureTooltip();
      unByKey(listener);
    },

    /**
     * Creates a new measure tooltip
     */
    createMeasureTooltip() {
      if (this.measureTooltipElement) {
        this.measureTooltipElement.parentNode.removeChild(
          this.measureTooltipElement
        );
      }
      this.measureTooltipElement = document.createElement("div");
      this.measureTooltipElement.className = "ol-tooltip ol-tooltip-measure";
      this.measureTooltip = new Overlay({
        element: this.measureTooltipElement,
        offset: [0, -15],
        positioning: "bottom-center",
      });
      this.map.addOverlay(this.measureTooltip);
    },

    clearMeasurements() {
      this.measurementsLayerSource.clear();
      this.map
        .getOverlays()
        .getArray()
        .slice(0)
        .forEach((overlay) => {
          this.map.removeOverlay(overlay);
        });
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);
      if (errorHandle && typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    disableDraw() {
      if (this.currentActionType !== "select") {
        this.drawLayerSource.clear();
      }
      this.map.removeInteraction(this.newObjectDraw);
    },

    setInitialPositionWindows() {
      this.$nextTick(() => {
        //current screen size
        let sizeXWindow = this.$refs.map.offsetWidth;

        //position of the windows aligned to the left
        let half = sizeXWindow / 2;
        let positionX = Math.round(half + half / 1.8);

        //cluster window position
        this.setInitialPositionCluster(positionX);

        //cable window position
        this.setInitialPositionCable(positionX);

        //fiberCircuit Window Position
        this.setInitialPositionFiberCircuit(positionX);

        //Processing Window Position
        this.setInitialPositionProcessing(positionX);

        //Path Inspector Window Position
        this.setInitialPositionPathInspector(positionX);

        // StreetView Window Positions
        if (this.showStreetView) {
          this.setInitialPositionCluster(Math.round(half + half / 2.3));
          this.setInitialPositionCable(Math.round(half + half / 2.3));
          this.setInitialPositionFiberCircuit(Math.round(half + half / 2.3));
          this.setInitialPositionProcessing(Math.round(half + half / 2.3));
          this.setInitialPositionPathInspector(Math.round(half + half / 2.3));
        }
      });
    },

    setInitialPositionCluster(positionX, positionY) {
      this.cluster.position.x = positionX;
      this.cluster.position.y = positionY;
    },
    setInitialPositionCable(positionX, positionY) {
      this.cable.position.x = positionX;
      this.cable.position.y = positionY;
    },
    setInitialPositionFiberCircuit(positionX, positionY) {
      this.fiberCircuit.position.x = positionX;
      this.fiberCircuit.position.y = positionY;
    },
    setInitialPositionProcessing(positionX, positionY) {
      this.processingWindow.position.x = positionX;
      this.processingWindow.position.y = positionY;
    },

    setInitialPositionPathInspector(positionX, positionY) {
      this.pathInspectorPosition.x = positionX;
      this.pathInspectorPosition.y = positionY;
    },

    startPathInspector() {
      this.disableMapClickEvents();
      unByKey(this.singleClickEvent);
      this.singleClickEvent = this.map.on("singleclick", (ev) => {
        this.contextMenuPixel = ev.pixel;
        this.contextMenuCoords = ev.coordinate;
        this.SET_TOOL_OBJECT_LAYER("pathInspector");
        this.getObjects(ev, this.selectedToolObjectLayer);
      });
    },

    getObjectListForPathInspector(features) {
        this.objectList.list = this.filterObjectList(features, [
          { key: "objectType", value: "technical_point" },
          { key: "objectType", value: "technical_site" },
          { key: "objectType", value: "technical_local" },
          { key: "objectType", value: "building" },
          { key: "objectType", value: "closure" },
        ]);
      if (this.isObjectListEmpty()) {
        bus.$emit("highlight-path-inspector-instructions");
      }
    },

    closePathInspector() {
      this.resetPathInspector();
      this.disableDraw();
      this.resetSelectedObjectLayer();
    },

    resetPathInspector() {
      this.drawLayerSource.clear();
    },

    createHighlightObjectSelectLayer() {
      this.highlightObjectLayerSource = new VectorSource({ wrapX: false });
      return new VectorLayer({
        source: this.highlightObjectLayerSource,
      });
    },

    /* Highlight for the objects selected in the Asset Reservation table */

    removeHighlightFromSelectObject(uuid) {
      let feature = this.highlightObjectLayerSource.getFeatureById(uuid);
      if (feature) {
        this.highlightObjectLayerSource.removeFeature(feature);
      }
    },

    highlightObjectSelected(data) {
      if (data && data.geoJson) {
        let features = new GeoJSON().readFeatures(data.geoJson, {
          featureProjection: "EPSG:3857",
        });
        let feature = features[0];
        feature.setId(data.uuid);
        let geometryType = feature.getGeometry().getType();

        let highlightStyle = {
          point: {
            radius: 10,
            strokeWidth: 1,
            fillColor: "#4fa2b7",
          },
          polygon: {
            strokeWidth: 1,
            fillColor: "#4fa2b7",
          },
          line: {
            strokeWidth: 6,
            strokeColor: "#4fa2b7",
            strokeWidthSecond: 5,
          },
        };

        feature.setStyle(
          this.getHighlightFeatureStyle(geometryType, highlightStyle)
        );
        this.highlightObjectLayerSource.addFeature(feature);
      }
    },

    /* Fiber Circuit Methods */

    createHighlightObjectFiberCircuitLayer() {
      this.fiberCircuit.highlightObjectLayerSource = new VectorSource({
        wrapX: false,
      });
      return new VectorLayer({
        source: this.fiberCircuit.highlightObjectLayerSource,
      });
    },

    highlightObjectFiberCircuitSelected(geoJson) {
      let features = new GeoJSON().readFeatures(geoJson, {
        featureProjection: "EPSG:3857",
      });
      let feature = features[0];

      let highlightStyle = this.getHighlightStyle();

      let geometryType = feature.getGeometry().getType();

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.fiberCircuit.highlightObjectLayerSource.addFeature(feature);
    },

    getHighlightStyle() {
      return {
        point: {
          radius: 15,
          strokeWidth: 2,
          fillColor: [255, 255, 255],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(250, 250, 250, 0.3)",
        },
        line: {
          strokeWidth: 6,
          strokeColor: "#FFC000",
          strokeWidthSecond: 5,
        },
      };
    },

    closeFiberCircuit() {
      this.fiberCircuit.open = false;
      this.fiberCircuit.uuid = null;
    },

    openFiberCircuit(data) {
      if (data) {
        this.fiberCircuit.open = true;
        if (data.prefix === "pt") {
          this.fiberCircuit.uuid = data.object.connectedFiber.fb_uuid;
        } else if (data.prefix === "fb") {
          this.fiberCircuit.uuid = data.object.fb_uuid;
        }
      } else {
        this.fiberCircuit.open = false;
      }
    },

    // Capture Image Map

    getScreenShotMap() {
      this.map.once("postcompose", (event) => {
        let mapCanvas = event.context.canvas;

        let element = document.getElementById("map");
        let height = element.offsetHeight;
        let width = element.offsetWidth;

        mapCanvas.style.width = width + "px";
        mapCanvas.style.height = height + "px";

        html2canvas(mapCanvas).then((canvas) => {
          canvas.toBlob((blob) => {
            navigator.clipboard.write([
              new ClipboardItem({ ["image/png"]: blob }),
            ]);
          });
        });
        this.$toasted.global.toast_success(this.$i18n.t("CaptureSuccess"));
      });

      this.setHomePrintState(false);

      this.map.renderSync();
    },

    /* Join cables Methods */
    closeJoinCableMenu() {
      this.closeObjectMenu();
      this.joinCableMenu.open = false;
      this.removeHighlightObjectJoinCablesMenu();
    },

    async openJoinCablesMenu() {
      try {
        let connections = await this.fetchConnectionsToJoinCables(
          this.selectedObjectIdentifiers.uuid
        );
        if (connections && connections.length > 0) {
          this.joinCableMenu.open = true;
          this.joinCableMenu.connections = connections;
          this.joinCableMenu.cableUuid = this.selectedObjectIdentifiers.uuid;
        } else {
          this.removeHighlightFromObjectOnLeave();
          this.$toasted.global.toast_error(
            this.$i18n.t("WithoutConnectionsToJoinCables")
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.objectMenu.open = false;
      }
    },

    createHighlightObjectJoinCablesLayer() {
      this.joinCableMenu.highlightObjectLayerSource = new VectorSource({
        wrapX: false,
      });
      return new VectorLayer({
        source: this.joinCableMenu.highlightObjectLayerSource,
      });
    },

    removeHighlightObjectJoinCablesMenu() {
      if (this.joinCableMenu.highlightObjectLayerSource) {
        this.joinCableMenu.highlightObjectLayerSource.clear();
      }
    },

    highlightObjectJoinCablesMenu(geoJson) {
      let features = new GeoJSON().readFeatures(geoJson, {
        featureProjection: "EPSG:3857",
      });
      let feature = features[0];

      let highlightStyle = this.getHighlightStyle();

      let geometryType = feature.getGeometry().getType();

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.joinCableMenu.highlightObjectLayerSource.addFeature(feature);
    },

    async reverseDirection() {
      try {
        await this.reverseCableDirection({ uuid:this.selectedObjectIdentifiers.uuid, force:false});
        this.$toasted.global.toast_success(
          this.$i18n.t("CableSuccessfullyInverted")
        );
      } catch (e) {
        // this.$toasted.global.toast_error(e.response.data.data);
        // console.error(e);
        if (e.response.data.data == "Please reset fiber configuration") {
          this.continueInvertCables.open = true;
        }
      }
    },

    async invertCables() {
      try {
        await this.reverseCableDirection({ uuid:this.selectedObjectIdentifiers.uuid, force:true});
        this.$toasted.global.toast_success(
          this.$i18n.t("CableSuccessfullyInverted")
        );
      } catch (e) {
        this.$toasted.global.toast_error(e.response.data.data);
        console.error(e);
      }
      this.closeInvertCablesWindow();
      // if (e.response.data.data[0] == "Please reset fiber configuration") {
      //   this.continueInvertCables.open = true;
      //   this.reverseCableDirection(this.selectedObjectIdentifiers.uuid);
      //   }
      //   console.log(e.response.data.data[0])
      // this.reverseCableDirection(this.selectedObjectIdentifiers.uuid);
      // this.$toasted.global.toast_success(
      //   this.$i18n.t("CableSuccessfullyInverted")
      // );
      // this.continueInvertCables.open = false;
    },

    closeInvertCablesWindow() {
      this.continueInvertCables.open = false;
    },

    // Cables Split Methods
    closeCablesSplitDialog() {
      this.closureWithoutTechPoint.technicalPoint = null;
      this.closureWithoutTechPoint.event = null;
      this.closureWithoutTechPoint.passingObjects = false;
      this.cablesSplitDialog.open = false;
    },

    confirmCablesSplit(splitCables) {
      if (
        splitCables.cable_uuid !== "NoAction" &&
        (splitCables.split === "pass-through" ||
          splitCables.split === "spliced")
      ) {
        this.technicalPointData.split_cable = splitCables;
      }
      this.closeCablesSplitDialog();
      this.openForm(this.selectedToolObjectLayer, "create");
    },

    openCablesSplitDialog(event) {
      this.technicalPointData = event;
      this.cablesSplitDialog.open = true;
    },

    updateURL(workspaceUUID, latitude, longitude, zoom = null) {
      if (
        this.$route.name === "home" &&
        (this.$route.params.lat !== latitude ||
          this.$route.params.long !== longitude ||
          (this.$route.params.zoom * 1.0).toFixed(2) !==
            (zoom * 1.0).toFixed(2))
      ) {
        let newZoom = parseFloat(zoom) % 1 === 0 ? zoom : zoom.toFixed(2);
        this.$router.replace({
          name: "home",
          params: {
            workspace_uuid: workspaceUUID,
            lat: latitude,
            long: longitude,
            zoom: newZoom,
          },
        });
      }
    },

    /**
     * Deletes the measured area and the respective tooltip
     */
    deleteMeasurementArea() {
      let features = this.measurementsLayerSource.getFeatures();
      for (const feature of features) {
        if (
          !!this.measurementAreaToDelete.getGeometry() &&
          feature.getGeometry().ol_uid ===
            this.measurementAreaToDelete.getGeometry().ol_uid
        ) {
          document
            .getElementById("tooltip-" + feature.getGeometry().ol_uid)
            .remove();
        }
      }
      this.measurementsLayerSource.removeFeature(this.measurementAreaToDelete);
    },

    /**
     * Adjusts the bulk parameters and updates the layer
     *
     * @param {Object} params - the object containing the updated parameters
     */
    adjustParameters(params) {
      this.drawLayerSource.clear();
      this.techPointsCoordinates = [];
      this.bulk_tp.numberOfTPs = params.numberOfTPs;
      this.bulk_tp.distanceBetweenTPs = params.distanceBetweenTPs;
      this.bulk_tp.createPathway = params.createPathway;
      this.$nextTick(() => {
        if (this.bulkFeature) {
          this.previewBulkTP();
        }
      });
    },

    /**
     * Creates a new Draw, a tooltip indicating the length of the line and
     * listens to events to add vertices and finish the draw.
     * After the draw ends it draws the points in the map
     */
    drawNewBulkTPLine(e) {
      this.lineStringWKT = [];
      this.techPointsCoordinates = [];
      this.bulkTPLineVerticesCoordinates = [];
      this.draw = this.drawNewLineString();
      this.createPathwayTooltip();
      this.draw.on("drawstart", () => this.addVertexToBulkLine(e));
      this.draw.startDrawing_(e);

      let listener = this.draw.sketchFeature_.values_.geometry.on(
        "change",
        (e) => {
          let coords = e.target.getCoordinates();
          if (coords.length > 1) {
            this.updatePathwayTooltip(coords[coords.length - 1]);
          }
        }
      );

      this.clickEvent = this.map.on("click", (click) =>
        this.addVertexToBulkLine(click)
      );

      this.rightClickEvent = this.map.on("contextmenu", () => {
        this.draw.removeLastPoint();
        this.bulkTPLineVerticesCoordinates.pop();
        if (this.bulkTPLineVerticesCoordinates.length === 0) {
          this.createPathwayTooltip();
        }
      });

      this.draw.on("drawend", () => {
        this.disableMapClickEvents();
        this.map.removeOverlay(this.pathwayTooltip);
        this.previewBulkTP();
      });
    },

    /**
     * Adds the vertex to the line
     */
    addVertexToBulkLine(e) {
      if (this.isClickInsideWorkspace(e)) {
        this.bulkTPLineVerticesCoordinates.push(e.coordinate);
      } else {
        this.newObjectDraw.removeLastPoint();
        this.loading = false;
      }
    },

    /**
     * Previews the bulk technical point line.
     * First it draws the bulk line, then removes interactions from the map and
     * then calculates the coordinates of the technical points
     */
    previewBulkTP() {
      if (this.bulkTPLineVerticesCoordinates.length > 1) {
        this.bulkFeature = new GeoJSON().readFeature({
          type: "Feature",
          id: 2130,
          geometry: {
            type: "LineString",
            coordinates: this.bulkTPLineVerticesCoordinates,
          },
        });
        this.lineStringWKT = new WKT().writeFeature(this.bulkFeature);
        this.drawLayerSource.addFeature(this.bulkFeature);
        this.map.removeInteraction(this.draw);
        this.calculateTechPointsCoordinates();
      } else {
        this.$toasted.global.toast_error("You must draw more than 1 point");
        this.$nextTick(() => {
          this.resetLineString();
        });
      }
    },

    /**
     * Calculates the coordinates of the technical points.
     * First it draws the vertices of the bulk line, then it draws the rest of
     * the technical points
     */
    calculateTechPointsCoordinates() {
      let coords;
      for (let i = 0; i < this.bulkTPLineVerticesCoordinates.length; i++) {
        coords = this.bulkTPLineVerticesCoordinates[i];
        this.addTechPointPreviewFeature(
          0,
          coords,
          this.bulkTPLineVerticesCoordinates.length,
          false
        );
      }

      let totalLineStringLength = getLength(this.bulkFeature.getGeometry());
      let numberOfTPs = this.getTotalNumberOfTechPoints(totalLineStringLength);
      this.bulkFeature.getGeometry().forEachSegment((start, end) => {
        let segmentLineString = new LineString([start, end]);
        numberOfTPs = this.getNumberOfTechPointsInSegment(
          getLength(segmentLineString),
          totalLineStringLength
        );
        for (let i = 0; i < Math.floor(numberOfTPs); i++) {
          coords = segmentLineString.getCoordinateAt((i + 1) / numberOfTPs);
          this.addTechPointPreviewFeature(
            end,
            coords,
            this.getTotalNumberOfTechPoints(totalLineStringLength),
            true
          );
        }
      });

      this.bulk_tp.tooManyPointsDrawn =
        this.techPointsCoordinates.length -
          this.bulkTPLineVerticesCoordinates.length >
        100;
    },

    /**
     * Returns the total number of technical points
     *
     * @param {Number} totalLineStringLength - the total length of the bulk line
     */
    getTotalNumberOfTechPoints(totalLineStringLength) {
      if (this.bulk_tp.distanceBetweenTPs) {
        let lineStringLength = totalLineStringLength;
        return lineStringLength / this.bulk_tp.distanceBetweenTPs;
      } else if (this.bulk_tp.numberOfTPs) {
        return this.bulk_tp.numberOfTPs;
      }
    },

    /**
     * Returns the number of technical points in a choosen segment
     *
     * @param {Number} segmentLineStringLength - the length of the segment
     * @param {Number} totalLineStringLength - the total length of the bulk line
     */
    getNumberOfTechPointsInSegment(
      segmentLineStringLength,
      totalLineStringLength
    ) {
      if (this.bulk_tp.distanceBetweenTPs) {
        return segmentLineStringLength / this.bulk_tp.distanceBetweenTPs;
      } else if (this.bulk_tp.numberOfTPs) {
        return (
          (this.bulk_tp.numberOfTPs * segmentLineStringLength) /
          totalLineStringLength
        );
      }
    },

    /**
     * Get client layers
     */
    getAllLayers() {
      return new Promise((resolve) => {
        this.getLayersMenu().then((allLayers) => {
          this.allLayers = allLayers;
          this.allLayers.shift();
          resolve();
        });
      });
    },

    /**
     * Adds a feature for the technical point to the layer and stores the coordinates in an array
     *
     * @param {Array} coords - an array with the tech point coordinates
     * @param {Number} numberOfTPs - the number of technical points drawn
     */
    addTechPointPreviewFeature(end, coords, numberOfTPs, bulkTP) {
      let vertexTPFeature = new Feature({
        geometry: new Point(coords),
      });
      if (Math.floor(numberOfTPs) <= 100) {
        vertexTPFeature.setStyle(this.bulkTechPointStyle);
        this.drawLayerSource.addFeature(vertexTPFeature);
      }
      if (!bulkTP) {
        this.techPointsCoordinates.push({
          text_geom: new WKT().writeFeature(vertexTPFeature),
          projection: 3857,
        });
      } else {
        const segmentEnd = new Feature({ geometry: new Point(end) });
        const endTextGeom = new WKT().writeFeature(segmentEnd);
        const endIndex = this.techPointsCoordinates
          .map((element) => element.text_geom)
          .indexOf(endTextGeom);
        const found = this.techPointsCoordinates.find(
          (element) =>
            element.text_geom === new WKT().writeFeature(vertexTPFeature)
        );
        if (!found) {
          this.techPointsCoordinates.splice(endIndex, 0, {
            text_geom: new WKT().writeFeature(vertexTPFeature),
            projection: 3857,
          });
        }
      }
    },

    /**
     * Closes the bulk creation dialog and resets the data
     */
    cancelBulkTPCreation() {
      this.bulk_tp.dialog = false;
      this.closeBulkTP();
    },

    /**
     * Opens the form for the selected technical point
     *
     * @param {String} techPoint - the chosen tech point layer
     */
    openSelectedTechPointForm(techPoint) {
      this.bulk_tp.dialog = false;
      this.openForm(techPoint, "create");
      if (this.bulk_tp.createPathway) {
        this.nextForm = "fc_pathway";
      }
    },

    /**
     * Creates the technical points and, if the user chose so, the pathway
     */
    async createBulkTechPoints() {
      this.loading = true;
      this.bulk_tp.techPointData.nodes = this.techPointsCoordinates;
      delete this.bulk_tp.techPointData.node;
      if (this.bulk_tp.pathwayData) {
        this.bulk_tp.pathwayData.text_geom = new WKT().writeFeature(
          this.bulkFeature
        );
      }

      try {
        let dataToSend = {
          techpoints: this.bulk_tp.techPointData,
          pathway: this.bulk_tp.pathwayData,
        };
        await this.createBulkTPs(dataToSend);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.updateGeoserver();
        this.closeBulkTP();
      }
    },

    async importExternalData(layers, serviceUUID) {
      if (!!this.wkt) {
        this.loading = true;
        try {
          let dataToSend = {
            text_geom: this.wkt,
            service_uuid: serviceUUID,
            layers: layers,
            action: "get_all_data",
          };
          await this.createNewImport(dataToSend);
          this.externalImport.successful = true;
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
          this.closeExternalImportWindow();
        }
      } else {
        this.$toasted.global.toast_warning(
          this.$i18n.t("StartDrawingPolygonInstruction")
        );
      }
    },

    openRedrawClusterDialog(uuid) {
      this.redrawClusterTile.open = true;
      this.redrawClusterTile.uuid = uuid;
      this.closeObjectMenu();
    },

    closeRedrawWindow() {
      this.redrawClusterTile.open = false;
      this.redrawClusterTile.uuid = null;
    },

    async redrawCluster() {
      try {
        this.loading = true;
        await this.callRedrawCluster(this.redrawClusterTile.uuid);
      } catch (e) {
        console.error(e);
        this.loading = false;
      } finally {
        this.updateGeoserver();
        this.loading = false;
        this.closeRedrawWindow();
      }
    },

    cancelRedirect() {
      this.externalImport.successful = false;
    },

    redirectToWorkspaceManagement() {
      this.$router.push({ name: "data-external" });
    },

    openBundleForm() {
      this.openForm("fc_microduct_bundle", "create");
    },
    openFloorboxForm(){
      this.openForm("fc_closure", "edit");
    },

    centerMapOnObject(objectGeoJSON) {
      let feature = new GeoJSON().readFeatures(objectGeoJSON, {
        featureProjection: "EPSG:3857",
      });
      let point = feature[0].getGeometry();
      this.mapView.fit(point, {
        padding: [150, 50, 50, 150],
        minResolution: 0.1,
      });

      this.highlightObjectOnHover(objectGeoJSON);
      this.addMarkPoint(feature[0]);
    },

    async getNetworkRuleMDU() {
      try {
        let workspace = await this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
          type: "id",
          value: sessionStorage.getItem("workspace_rules_id")
        });
        this.minNumberHPsPerMDU = workspace.data[0].data.mdu_classifier;
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style>
.v-progress-linear {
  background: white;
}
</style>

<style scoped>
.dialog-box .secondary-button {
  width: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/MapT.vue